/**
 * `AIOrchestratorModel.ts` — port of Unity's `AIOrchestratorModel` (+ Core
 * partial responsibilities that apply on web).
 *
 * Composition root for the 7-layer harness: constructs registry, executors,
 * context engine, guardrails, observability, interface model, and the
 * realtime session host (Unity `InitializeRealtime` + `StartRuntime`).
 * Owns prompts, personality, session policy (instructions + project
 * context + per-turn user context), tool availability, and context-refresh
 * routing. Routing extras (wait_for auto-turns, playback waits) are
 * deferred with the actions port.
 */

import { Emitter } from "@core/events/emitter";
import { canAuthorProject } from "@models/data/userRolePermissions";
import { Ioc } from "@core/ioc/ioc";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IConsolePlacementModel } from "@models/xr/IConsolePlacementModel";
import { SCENE_MODEL, type ISceneModel } from "@models/scene/ISceneModel";
import {
  PERMISSIONS_MODEL,
  type IPermissionsModel,
} from "@models/permissions/IPermissionsModel";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import type { IPlacementModel } from "@models/tools/IPlacementModel";
import type { IExplosionModifierModel } from "@models/modifiers/IExplosionModifierModel";
import type { IMaterialModel } from "@models/materials/IMaterialModel";
import type { IProjectListModel } from "@models/project/IProjectListModel";
import type { IUndoRedo } from "@models/project/IUndoRedo";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { ITelemetryService } from "@models/telemetry/ITelemetryService";
import { PICKING_SERVICE, type PickingService } from "@runtime/picking/pickingService";
import { resolveRevertCamera } from "@runtime/camera/revertCameraPose";
import { resolveEntityAnimationPlayback } from "@runtime/assets/entityAnimationPlayback";
import { XR_SESSION_SERVICE, type XRSessionService } from "@runtime/xr/XRSessionService";
import type {
  ISparkBehavior,
  ISparkBehaviorHost,
} from "@runtime/assets/sparkBehavior";

import { isAiEnabled, isRealtimeFamilyModel } from "@ai/aiConfig";
import {
  getMainAgentInstructions,
  getPersonality,
  DEFAULT_PERSONALITY_ID,
  DEFAULT_ROLE_ID,
  PLAYBACK_ROLE_ID,
  WORKFLOW_BUILDER_ROLE_ID,
} from "@ai/guardrails/agentPrompts";
import { ensureBasePromptLoadedAsync } from "@ai/guardrails/AIRealtimeBasePromptCatalog";
import { ensurePersonalitiesLoadedAsync } from "@ai/guardrails/AIRealtimePersonalityCatalog";
import type { IProjectWorkflowModel, WorkflowChangedEvent } from "@models/workflow/IProjectWorkflowModel";
import type { IWorkflowActivityService } from "@models/workflow/IWorkflowActivityService";
import type { IDevSettingsModel } from "@models/ui/IDevSettingsModel";
import type { IAppSettingsModel } from "@models/settings/IAppSettingsModel";
import type { IAIPromptStoreModel } from "@ai/guardrails/IAIPromptStoreModel";
import type {
  AITranscriptContext,
  AITranscriptInputType,
  IAITranscriptService,
} from "@ai/transcript/IAITranscriptService";
import {
  WorkflowScriptRunner,
  type WorkflowScriptHost,
} from "@models/workflow/WorkflowScriptRunner";
import { WorkflowRunModel } from "@models/workflow/WorkflowRunModel";
import type { WorkflowRunStart, WorkflowStepArrival } from "@models/workflow/IWorkflowRunModel";
import { resolveUserFirstName } from "@models/workflow/workflowTextTokens";
import { WorkflowExecutor } from "@ai/executor/Workflow_Executor";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import { resolveManyByPath } from "@ai/context/hierarchyPaths";
import { AIAnswerAttributionModel } from "@ai/context/AIAnswerAttribution";
import { getString, getStringList, type ToolArgs } from "@ai/executor/AgentToolExecutorModel";
import {
  highlightFinitePulseDurationSeconds,
  PersistentHighlightPulseModes,
  parsePersistentHighlightJson,
} from "@models/data/persistentHighlightData";
import {
  AIAgentToolRegistryModel,
  AIAgentToolsModel,
  TOOL_TRIGGER,
  type IAIAgentToolsModel,
} from "@ai/registry/AIAgentToolRegistryModel";
import {
  AgentToolExecutorModel,
  type AgentToolExecutorHost,
} from "@ai/executor/AgentToolExecutorModel";
import { EntitiesExecutor } from "@ai/executor/Entities_Executor";
import { ScenesExecutor } from "@ai/executor/Scenes_Executor";
import { AssetsExecutor } from "@ai/executor/Assets_Executor";
import { SelectionSetsExecutor } from "@ai/executor/SelectionSets_Executor";
import { ProjectExecutor } from "@ai/executor/Project_Executor";
import { ContextExecutor } from "@ai/executor/Context_Executor";
import { DocsExecutor } from "@ai/executor/Docs_Executor";
import { MemoryExecutor } from "@ai/executor/Memory_Executor";
import { normalizeProductNameKey } from "@models/data/productDocumentDto";
import {
  PROJECT_DOCS_MODEL,
  type IProjectDocsModel,
} from "@models/docs/IProjectDocsModel";
import { buildDocumentNameIndex } from "@models/docs/documentDisplayNames";
import type { ICustomerVectorStoreModel } from "@ai/memory/ICustomerVectorStoreModel";
import { AIBehaviourExecutor } from "@ai/executor/AIBehaviour_Executor";
import {
  AIContextEngineModel,
  type IAIContextEngineModel,
} from "@ai/context/AIContextEngineModel.Core";
import { AIGuardrailsModel, type IAIGuardrailsModel } from "@ai/guardrails/AIGuardrailsModel";
import { AIObservabilityModel } from "@ai/observability/AIObservabilityModel";
import {
  RealtimeConversationLog,
  type RealtimeLogRecordedEvent,
} from "@ai/observability/RealtimeConversationLog";
import { AIConversationPresentationModel } from "@ai/interface/AIConversationPresentationModel";
import { AIInterfaceModel, type AIFileInputResult } from "@ai/interface/AIInterfaceModel";
import { AIRealtimeSessionModel } from "./AIRealtimeSessionModel";
import { ResponsesTurnBridge } from "./ResponsesTurnBridge";
import { buildWorkflowRunStartNote, buildWorkflowRunUpdateNote } from "./workflowRunUpdateNote";
import { toolSpokenReply } from "@ai/executor/toolTurnHandover";
import { formatInternalSay } from "./internalSay";
import {
  BOUNDING_BOX_TOOL_MODEL,
  type IBoundingBoxToolModel,
} from "@models/tools/IBoundingBoxToolModel";
import {
  RESPONSES_ATTACHMENT_ACCEPT,
  ResponsesFileInputModel,
} from "./responsesFileInput";
import type {
  IRealtimeSessionPolicyProvider,
  RealtimeSessionConnectionConfig,
  RealtimeSessionOptions,
} from "./RealtimeSession";
import type { RealtimeTransportKind } from "./IRealtimeTransport";
import {
  K_TRANSPORT_FAILURE_CODES,
  K_WEBSOCKET_FALLBACK_FAILURE_CODES,
} from "./IRealtimeTransport";
import { resolveRealtimeReasoningEffort } from "@models/data/appSettingsDto";
import {
  ensureVoiceSettingsLoadedAsync,
  getVoiceSettings,
} from "@models/data/voiceSettings";

const PREF_PERSONALITY = "Campfire.AI.PersonalityId";

/**
 * How long a workflow script's `say` waits for the realtime session to become
 * able to carry it before giving up and playing the step silently.
 *
 * Sized for a RELOAD, which is the case that needs it: the orchestrator awaits
 * three prompt-catalog fetches, then mints a client secret, then completes the
 * transport handshake, then waits ~450 ms for `session.created` — typically
 * 2-3 s, more on a poor link.
 *
 * ⚠ The trade, stated: where the session is genuinely never coming up (AI
 * enabled but broken), every say step now pauses for this instead of the 5 s
 * the response-idle grace already burned on a dropped line. That case has no
 * speech either way; the bound is what keeps it from becoming a hang. AI
 * switched OFF costs nothing — `waitUntilReadyForUserInputAsync` refuses
 * immediately.
 */
const K_SPEECH_READY_TIMEOUT_MS = 10_000;
/**
 * How long Spark holds a part a tool just acted on before returning to the
 * user (user direction, 2026-09-02).
 *
 * ⚠ Without it a tool's gaze is released by exactly one thing —
 * `onAssistantTextCompleted` — which a WORKFLOW script command never fires, so
 * an authored `isolate` step left Spark staring at that part for the rest of
 * the run. The hold is what makes the glance mean the same thing whoever ran
 * the tool.
 *
 * Releasing to the USER rather than to whatever the gaze was on before is
 * deliberate: it is this app's own resting gaze and the exact release
 * `onAssistantTextCompleted` already performs, so a lapsed hold and a finished
 * turn leave Spark in the same place. Restoring the previous target was the
 * alternative and needs a second stored node that can be stale by the time it
 * is restored (a scene change disposes it), for a difference nobody asked for.
 */
const K_SPARK_TOOL_GAZE_HOLD_S = 2;

/**
 * The project editor's default factual-answering guidance. Loaded locally on
 * project entry, and again on every role switch back into its scope, so a
 * question does not spend a full realtime round on `Load_Skill`; still subject
 * to the deployment gate and active-role scope.
 */
const K_PROJECT_DEFAULT_SKILL_ID = "answer";

/**
 * How long the project-entry hold waits for the active scene once the project
 * itself has arrived. Only covers a scene that never activates; the normal gap
 * is well under a second. See {@link AIOrchestratorModel.beginProjectEntrySettling}.
 */
const K_PROJECT_ENTRY_SETTLE_TIMEOUT_MS = 5000;

/**
 * Ceiling on how long a step's arrival holds context refreshes back for its
 * opening narration. The narration normally goes out within the scene switch's
 * data phase (well under a second); this only covers a script that never gets
 * to speak, so the instructions cannot stay a step behind for long. See
 * {@link AIOrchestratorModel.openNarrationRefreshHold}.
 */
const K_NARRATION_REFRESH_HOLD_MAX_MS = 3000;

/**
 * Part-path arg keys scanned for Spark look-at (`partPathPatterns`).
 *
 * ⚠ Keys the WEB registry does not carry are kept deliberately — they name
 * Unity tools this port has not registered yet (measurements), and
 * dropping them means the gaze silently stops working the day one lands. A key
 * that matches nothing costs one failed lookup.
 */
const K_SPARK_PART_PATH_ARG_KEYS: ReadonlyArray<string> = [
  "partPathsToMove",
  "partPathsToHide",
  "partPathsToUnhide",
  "partPathsToIsolate",
  "partPathsToSelect",
  "partPathsToFocus",
  "partPathsToLookAt",
  "partPathsToHighlight",
  "partPathsToClearHighlight",
  "partPathsToSpin",
  "partPathsToClearSpin",
  "partPathsToColor",
  "partPathsToRename",
  "partPathsToReset",
  // `apply_material_on_parts`, `create_selection_set`, `Get_Part_Materials`.
  "partPaths",
  // `delete_model` — a whole model is as much a look-at target as a part.
  "modelPaths",
  "targetPartPath",
  "startPartPath",
  "endPartPath",
  "modelPathsToExplode",
];

function isWorkflowKnowledgeChangeOrigin(origin: WorkflowChangedEvent["origin"]): boolean {
  return origin === "load" ||
    origin === "import" ||
    origin === "builder" ||
    origin === "editor" ||
    origin === "api";
}

export interface AIOrchestratorDeps {
  readonly project: IProjectModel;
  readonly selection: ISelectionModel;
  readonly placement: IPlacementModel;
  readonly explosion: IExplosionModifierModel;
  readonly materials: IMaterialModel;
  readonly projectList: IProjectListModel;
  readonly undoRedo: IUndoRedo;
  readonly auth: IAuthModel;
  readonly telemetry: ITelemetryService;
  readonly workflow: IProjectWorkflowModel;
  readonly workflowActivity: IWorkflowActivityService;
  readonly transcript: IAITranscriptService;
  /**
   * The published prompt set for this deployment — role / skill / base /
   * personality bodies, replacing the build's own when one is published.
   * Superseded the earlier `promptOverrides` dependency, which was a
   * debug-gated per-browser shadow rather than a replacement.
   */
  readonly prompts: IAIPromptStoreModel;
  /** Debug-gated product overrides, including Responses reply presentation. */
  readonly devSettings: IDevSettingsModel;
  /**
   * The account's shared settings — here for the SKILL GATE (a skill switched
   * off there is filtered out of `AIInterfaceModel.availableSkills`, so it never
   * reaches the prompt index or `Load_Skill`) and for the per-role session
   * MODEL config (`getRoleModel`, resolved by `resolveSessionConnection`).
   */
  readonly appSettings: IAppSettingsModel;
  /**
   * Console placement, for `requires_console_placement` — the workflows that
   * seat the stage in the wearer's real room before they play.
   */
  readonly consolePlacement: IConsolePlacementModel;
  /** Product-document discovery stays in the docs model; indexing is owned here. */
  readonly docs: IProjectDocsModel;
  readonly vectorStore: ICustomerVectorStoreModel | null;
}

export class AIOrchestratorModel
  implements IRealtimeSessionPolicyProvider, ISparkBehaviorHost
{
  readonly onCurrentPersonalityChanged = new Emitter<string>();
  readonly onCurrentRoleChanged = new Emitter<string>();

  readonly interfaceModel: AIInterfaceModel;
  readonly toolsModel: IAIAgentToolsModel;
  readonly contextEngineModel: IAIContextEngineModel;
  readonly guardrailsModel: IAIGuardrailsModel;
  readonly observabilityModel: AIObservabilityModel;
  /** Developer wire log (web port of `RealtimeConversationFileLogger`). */
  readonly conversationLog: RealtimeConversationLog;
  readonly presentationModel: AIConversationPresentationModel;
  readonly realtimeSessionModel: AIRealtimeSessionModel;
  /** Workflow run sequencer + script runner (Unity: `WorkflowRunModel` owned by the orchestrator). */
  readonly workflowRunModel: WorkflowRunModel;
  readonly workflowScriptRunner: WorkflowScriptRunner;

  /**
   * Responses text bridge — answers the active role's turns on a non-realtime
   * model (shipped: `workflow-builder` → gpt-5.6-sol / medium effort) while
   * the realtime session stays connected as the voice surface. See
   * `ResponsesTurnBridge.ts` for why the websocket cannot do this itself.
   */
  private readonly m_textBridge: ResponsesTurnBridge;
  /**
   * Responses-side file inputs for the bridged conversation. Lives beside the
   * bridge (same lifetime, same reset points) because the file ids it hands out
   * are only meaningful while the history referencing them is alive.
   */
  private readonly m_fileInput = new ResponsesFileInputModel();

  private readonly m_deps: AIOrchestratorDeps;
  private m_personalityId: string;
  private m_roleId: string = DEFAULT_ROLE_ID;
  /**
   * Whether a workflow run was playing at the last run-state event —
   * `onRunStateChanged` also fires on every step move, and only the
   * started/stopped EDGES change the role. See `applyWorkflowAgentRole`.
   */
  private m_isWorkflowRunPlaying = false;
  /**
   * The run-state bullets as the live session last had them, so a change can be
   * told from an emit that moved nothing. Seeded null because that is what the
   * builder answers with no workflow loaded, which is the state at
   * construction. See `handleWorkflowRunStateChanged`.
   */
  private m_sessionRuntimePrompt: string | null = null;
  /** Whether the Workflow editor panel is open — see `setWorkflowEditorOpen`. */
  private m_isWorkflowEditorOpen = false;
  /**
   * The role to hand back once neither workflow surface owns one; null when
   * neither the editor nor playback ever took it.
   */
  private m_roleBeforeWorkflow: string | null = null;
  /**
   * `model|effort` of the last session-model resolution, so the shared-settings
   * `onChanged` firehose (skill toggles, prompt uploads, …) only touches the
   * session when the MODEL config actually moved. "" = never resolved.
   */
  private m_sessionModelKey = "";
  /** Project entry is in flight; see `beginProjectEntrySettling`. */
  private m_isProjectEntrySettling = false;
  /** A context refresh arrived during the hold and still has to be sent. */
  private m_hasHeldProjectEntryRefresh = false;
  /** The project context + Answer preload finished. */
  private m_isProjectEntryContextReady = false;
  /** The project's first scene became active. */
  private m_isProjectEntrySceneReady = false;
  private m_projectEntrySettleTimer: ReturnType<typeof setTimeout> | null = null;
  /** A step just arrived and its script has not acted yet; see `openNarrationRefreshHold`. */
  private m_isNarrationRefreshHeld = false;
  /** A context refresh arrived during that hold and still has to be sent. */
  private m_hasHeldNarrationRefresh = false;
  private m_narrationRefreshHoldTimer: ReturnType<typeof setTimeout> | null = null;
  /** The step the hold was last decided for, so only an ARRIVAL opens one. */
  private m_narrationHoldStepName: string | null = null;
  /**
   * Model ids the live realtime endpoint refused at connect (`invalid_model` —
   * e.g. a Responses-family model like gpt-5.6-sol, which the endpoint rejects
   * with "not supported in realtime mode"). A rejected model falls back to the
   * stock realtime config instead of looping Spark offline on reconnect.
   * App-session lifetime: a reload retries, so platform support lands without
   * a settings change.
   */
  private readonly m_rejectedSessionModels = new Set<string>();
  /**
   * Model ids that refused a `reasoning.effort` key on `session.update`
   * (`invalid_value` "Unsupported option for this model" — the realtime-mode
   * models do, measured 2026-08-13). The refusal voids the WHOLE update, so
   * instructions and tools never land; dropping the key and resending is the
   * only working shape.
   */
  private readonly m_effortRejectedModels = new Set<string>();
  /**
   * Set once the endpoint has refused the transcription language key.
   * They are then dropped for the app session — a session that transcribes
   * imperfectly is worth far more than one whose `session.update` is voided,
   * taking its instructions and tool schemas with it.
   */
  private m_transcriptionLanguageRejected = false;
  /**
   * WebRTC connectivity/capability failures that select WebSocket for the rest
   * of this app session. Deliberately excludes `realtime_sdp_failed`: mint,
   * authorization, endpoint and SDP refusals must remain visible and retry the
   * configured WebRTC transport instead of being masked by a second path.
   */
  private readonly m_rejectedTransports = new Set<RealtimeTransportKind>();
  /**
   * Set when the MODEL self-heal has just fired for the current connect, so the
   * error handler can skip the `realtime_sdp_failed` that follows it.
   *
   * A REFUSED MODEL is not a broken transport. Emission is synchronous and
   * ordered (the server's own code, then `realtime_sdp_failed`), so a one-shot
   * consumed by the next transport code is enough; it is also cleared whenever
   * a session actually connects, so it can never suppress a later, genuine
   * transport failure.
   */
  private m_modelFallbackJustFired = false;
  private readonly m_offFns: Array<() => void> = [];

  // Spark 3D head (Unity `m_sparkBehavior` on `AIOrchestratorModel.cs`).
  private m_sparkBehavior: ISparkBehavior | null = null;
  private m_sparkHiddenByUser = false;
  private m_keepSparkFocusOnToolTargetAfterResponse = false;

  constructor(deps: AIOrchestratorDeps) {
    this.m_deps = deps;
    this.m_personalityId =
      safeReadPref(PREF_PERSONALITY) ?? DEFAULT_PERSONALITY_ID;

    // --- Layer construction (Unity `InitializeRealtime`). -------------
    this.observabilityModel = new AIObservabilityModel(deps.auth, deps.project, deps.telemetry);
    this.conversationLog = new RealtimeConversationLog((event) =>
      this.recordRealtimeLogEvent(event),
    );
    this.presentationModel = new AIConversationPresentationModel();
    this.contextEngineModel = new AIContextEngineModel(
      deps.project,
      deps.selection,
      () => this.isWriter(),
    );
    this.guardrailsModel = new AIGuardrailsModel(
      () => this.isWriter(),
      this.presentationModel,
      () => this.interfaceModel.requestListenUser(),
      () => deps.project.isWorkflowPlaybackMutationScopeActive,
      // Read per call: the runner is constructed further down this constructor,
      // and no tool can run before it finishes.
      () => this.workflowScriptRunner.isCapturingLiveChanges,
      (summary) => this.conversationLog.note("step_move.check", summary),
    );

    const registry = new AIAgentToolRegistryModel();
    const executor = new AgentToolExecutorModel(this.guardrailsModel, this.observabilityModel);
    this.toolsModel = new AIAgentToolsModel(registry, executor);

    this.realtimeSessionModel = new AIRealtimeSessionModel(
      this.toolsModel,
      executor,
      this,
      this.observabilityModel,
      this.conversationLog,
    );

    this.interfaceModel = new AIInterfaceModel(
      this.realtimeSessionModel,
      this.presentationModel,
      this.observabilityModel,
      (toolName) => this.toolsModel.getLabelWhenActive(toolName),
      deps.prompts,
      deps.appSettings,
      this.conversationLog,
    );

    this.m_textBridge = new ResponsesTurnBridge({
      buildInstructions: () => this.buildMainAgentInstructionsWithProjectContext(),
      buildTurnContext: () => this.contextEngineModel.tryBuildUserContextForMessage(),
      // The SAME role/skill-gated schemas the realtime session carries — the
      // bridge is the main agent on another transport, not a second tool gate.
      buildToolSchemas: () => this.toolsModel.getEnabledToolSchemas(),
      resolveModel: () => {
        const config = this.m_deps.appSettings.getRoleModel(this.m_roleId);
        return {
          model: config.model ?? getVoiceSettings().realtime.model,
          reasoningEffort: config.reasoningEffort,
          // Inert here: the bridge is plain HTTP `/v1/responses` and has no
          // transport to choose. It shares the type because it shares the
          // model + effort resolution, not because it connects.
          transport: "websocket" as const,
        };
      },
      getUsageSource: () => this.interfaceModel.activeRole?.displayName ?? "Text Bridge",
      recordUsage: (source, delta) => this.realtimeSessionModel.addResponsesApiUsage(source, delta),
      executeToolAsync: (name, args) => executor.executeAsync(name, args, TOOL_TRIGGER.AI),
      deliverReply: (text) => {
        // Two gates, and both must allow it: the ROLE's own `voice`
        // (`workflow-builder` ships off — its author reads the document rather
        // than listening to it) and the debug panel's global override.
        const withVoice =
          this.m_deps.appSettings.getRoleModel(this.m_roleId).voice &&
          this.m_deps.devSettings.isResponsesVoiceEnabled;
        this.conversationLog.note("bridge.reply", withVoice ? "mode=voice" : "mode=text");
        if (withVoice) {
          // Spoken + displayed through the realtime session's INTERNAL SAY
          // contract (the base prompt's Internal Speech Messages — the same
          // path workflow script playback uses).
          this.realtimeSessionModel.sendUserText(`INTERNAL SAY: ${text}`, "text");
        } else {
          // Publish the Responses model's own text directly. Realtime roles
          // never reach this bridge, so their voice behavior is unchanged.
          this.realtimeSessionModel.notifyExternalAssistantText(text);
        }
      },
      setThinking: (thinking) => this.realtimeSessionModel.notifyExternalThinking(thinking),
      onToolStarted: (toolName) =>
        this.realtimeSessionModel.notifyExternalToolExecutionStarted(toolName),
      onToolCompleted: (toolName, result) =>
        this.realtimeSessionModel.notifyExternalToolExecutionCompleted(toolName, result),
      onReasoningStarted: () => this.realtimeSessionModel.notifyExternalReasoningStarted(),
      onReasoningDelta: (delta) => this.realtimeSessionModel.notifyExternalReasoningDelta(delta),
      onReasoningCompleted: (durationMs) =>
        this.realtimeSessionModel.notifyExternalReasoningCompleted(durationMs),
      reportError: (code) => this.realtimeSessionModel.notifyExternalError(code),
      log: this.conversationLog,
    });

    const host: AgentToolExecutorHost = {
      sendUserText: (text) => this.realtimeSessionModel.sendUserText(text, "text"),
      sendUserImage: (dataUrl) => this.realtimeSessionModel.sendUserImage(dataUrl),
      resetConversation: () => this.resetConversation(),
      setPersonality: (id) => this.setAgentPersonality(id),
      getCurrentRole: () => this.currentRole,
      executeCompanionToolAsync: async (toolName, args, triggeredBy) => {
        this.realtimeSessionModel.notifyExternalToolExecutionStarted(toolName);
        this.conversationLog.logTool(toolName, "call", args);
        let result: string;
        try {
          result = await executor.executeAsync(toolName, args, triggeredBy);
        } catch (error) {
          result = `error: ${error instanceof Error ? error.message : String(error)}`;
        }
        this.realtimeSessionModel.notifyExternalToolExecutionCompleted(toolName, result);
        this.conversationLog.logTool(toolName, "result", result);
        return result;
      },
      setAgentRole: (roleId) => this.setAgentRole(roleId),
      setConstantListening: (enabled) =>
        this.interfaceModel.setConstantListeningEnabled(enabled),
      requestListenUser: () => this.interfaceModel.requestListenUser(),
      setPanelMinimalMode: (hidden) =>
        this.interfaceModel.setShowPanelOnlyForResponsesAndCitations(hidden),
      setDebugMode: (enabled) => this.interfaceModel.setDebugMode(enabled),
      setSparkVisibility: (visible) => this.setSparkVisibility(visible),
      conversationPresentation: this.interfaceModel.conversationPresentation,
      // Helper-subagent usage lands in the same per-source tracker the
      // Workflow Builder uses (Unity `TrackHelperAgentUsage`).
      trackHelperAgentUsage: (source, usage) => {
        if (usage) this.realtimeSessionModel.addResponsesApiUsage(source, usage);
      },
      // A citation names the uploaded FILE; the library names the DOCUMENT.
      // Cached via `model.items`, so on the success path this is ONE Dynamo read
      // per session rather than one per answer. ⚠ Only on that path: a failed
      // load leaves `items` empty, so every later publish retries. Deliberate -
      // a transient miss must not blank the chips for the rest of the session -
      // and a failure degrades to the file name rather than costing the user
      // their sources either way.
      loadDocumentNameIndexAsync: async () => {
        if (!Ioc.isRegistered<IProjectDocsModel>(PROJECT_DOCS_MODEL)) return new Map();
        try {
          const model = Ioc.resolve<IProjectDocsModel>(PROJECT_DOCS_MODEL);
          const items = model.items.length > 0 ? model.items : await model.loadAsync();
          return buildDocumentNameIndex(items);
        } catch (error) {
          console.warn("[AIOrchestrator] document name index unavailable", error);
          return new Map();
        }
      },
    };

    // --- Workflow run/playback (Unity: WorkflowRunModel owned here). ---
    const scriptHost: WorkflowScriptHost = {
      // ⚠ `TOOL_TRIGGER.WORKFLOW` is what exempts an authored script command
      // from the `writerOnly` role gate, so a VIEWER can be guided. Spelling it
      // as a literal here would silently re-break that the day the guardrail's
      // side of the comparison changed.
      // Every host action below ends an arrival's narration hold — see
      // `openNarrationRefreshHold` — and each releases it BEFORE acting, so the
      // action always runs on the arrived step's instructions.
      executeToolAsync: (toolId, args) => {
        this.releaseNarrationRefreshHold();
        return executor.executeAsync(toolId, args, TOOL_TRIGGER.WORKFLOW);
      },
      // "INTERNAL SAY:" rides the base prompt's Internal Speech Messages contract — the model
      // speaks the text verbatim (Unity `SparkRecordAndPlay.BuildVerbatiumActionState` parity).
      // `origin: "script"` is what lets the panel tell this line from an answer.
      // The workflow panel already shows the step's instructions, so repeating
      // the spoken copy in the conversation under them is the same text twice
      // (user direction, 2026-09-01) — and the `INTERNAL SAY:` prefix cannot
      // stand in for the flag, because the Responses bridge delivers real
      // answers through it too.
      sayVerbatim: (text) => {
        // The held update goes FIRST, so the line is voiced on the arrived
        // step's instructions; `sendUserText`'s own refresh then finds nothing
        // new to send.
        this.releaseNarrationRefreshHold();
        // Tagged with the language the learner asked for, when it is not the
        // one the workflow is written in — read per line, so a switch mid-run
        // (or a "this step only" language ending) reaches the very next say
        // (see `internalSay.ts`).
        this.realtimeSessionModel.sendUserText(
          formatInternalSay(
            text,
            this.workflowRunModel.translationLanguage,
            this.workflowRunModel.workflowLanguage,
          ),
          "text",
          "script",
        );
      },
      // The say's own precondition. Owned here rather than passed in by the
      // runner: how long a session takes to come up is the AI layer's
      // knowledge, and the lesson's only concern is whether the line was
      // spoken.
      waitForSpeechReadyAsync: (signal) =>
        this.realtimeSessionModel.waitUntilReadyForUserInputAsync(
          K_SPEECH_READY_TIMEOUT_MS,
          signal,
        ),
      waitForResponseIdleAsync: (timeoutMs) => this.waitForResponseIdleAsync(timeoutMs),
      // Barge-in when the run moves: whatever Spark is saying belonged to the
      // step the learner just left. Through the INTERFACE model rather than
      // `realtimeSessionModel` — `src/ai/CLAUDE.md` keeps playback off that
      // seam — and it reaches the Responses bridge's turn as well, which is
      // what a learner-asked question is being answered on under a
      // non-realtime role.
      stopSpeaking: () => {
        this.interfaceModel.forceStop();
      },
      waitForToolEffectsIdleAsync: (toolId, args) =>
        this.waitForWorkflowToolEffectsIdleAsync(toolId, args),
      playCurrentStepVideo: (stepName) => {
        this.releaseNarrationRefreshHold();
        // The closure is installed before `workflowRunModel` is constructed but
        // cannot be invoked until a run later starts. Guard the owning step as
        // well: a delayed media mount from a step the learner left must never
        // start the next step's video.
        if (this.workflowRunModel.getPlaybackView()?.stepName !== stepName) {
          return { outcome: "not_running" };
        }
        return this.workflowRunModel.controlStepMedia({ kind: "play_from_script" });
      },
      getUserTurnCount: () => this.m_userTurnCount,
      // For the `<user name>` token in a `say:` line. Read per call, not
      // captured: the orchestrator outlives a sign-out.
      getUserFirstName: () => resolveUserFirstName(deps.auth.currentUser),
      waitForUserTurnAsync: (sinceTurnCount, timeoutMs, signal) => {
        this.releaseNarrationRefreshHold();
        return this.waitForUserTurnAsync(sinceTurnCount, timeoutMs, signal);
      },
    };
    this.workflowScriptRunner = new WorkflowScriptRunner(scriptHost, {
      project: deps.project,
      selection: deps.selection,
      explosion: deps.explosion,
      // Resolved per call, like the headset probe below: the viewport mounts
      // and unmounts under an orchestrator that outlives both.
      camera: () => resolveRevertCamera(),
      // A clip's pose for revert, and the take restart a replay needs (AI-1459).
      animation: () => resolveEntityAnimationPlayback(),
    });
    this.workflowRunModel = new WorkflowRunModel(
      deps.workflow,
      deps.project,
      this.workflowScriptRunner,
      // Which autostart flag applies. Resolved LAZILY, per call: the XR service
      // registers in the core pass but this orchestrator can outlive a
      // re-registration, and the answer changes when a session starts.
      //
      // `isPresenting || isHeadsetBrowser` rather than `isPresenting` alone,
      // because on a headset the session is requested at project-CLICK and only
      // adopted when the viewport mounts — the workflow's load event races that,
      // so a session-only test would read a headset as a desktop about half the
      // time. `isHeadsetBrowser` is a device fact, stable from the first frame.
      () => {
        if (!Ioc.isRegistered(XR_SESSION_SERVICE)) return false;
        const xr = Ioc.resolve<XRSessionService>(XR_SESSION_SERVICE);
        return xr.isPresenting || xr.isHeadsetBrowser;
      },
      { service: deps.workflowActivity, auth: deps.auth },
      {
        model: deps.consolePlacement,
        // `isPresenting` ALONE, unlike the headset probe above: opening the
        // placement mode without a live session flips a flag whose controller
        // is gated on `isPresenting`, so nothing would move and nothing would
        // ever end it — the run would wait forever. Lazy for the same reason
        // as the probe above.
        isAvailable: () => {
          if (!Ioc.isRegistered(XR_SESSION_SERVICE)) return false;
          return Ioc.resolve<XRSessionService>(XR_SESSION_SERVICE).isPresenting;
        },
      },
      // A step's `step_view` camera framing. Lazy like the two probes above,
      // and NOT an `AIOrchestratorDeps` member for the same reason
      // `ScenesExecutor` resolves it per call: `SCENE_MODEL` is registered
      // unconditionally at bootstrap, so there is nothing to test and nothing
      // to hold — what changes is whether a viewport has attached its camera,
      // which the scene model answers itself.
      () => Ioc.resolve<ISceneModel>(SCENE_MODEL),
      // Permissions, for the two VIEWER rules: a learner who cannot author this
      // project autostarts the workflow whatever the author's flags say, and
      // leaves the project when it finishes. Lazy for the reason the run model
      // states — the model is registered in the core pass and only gets its
      // auth/project transport later, so a reference captured here would answer
      // from least-privilege defaults. Guarded because the token is registered
      // unconditionally at bootstrap but this orchestrator can be built by a
      // harness that registered less.
      () => (Ioc.isRegistered(PERMISSIONS_MODEL)
        ? Ioc.resolve<IPermissionsModel>(PERMISSIONS_MODEL)
        : null),
    );
    // A run the learner asked to hear in another language: the agent's lookup
    // results say so too, where the answer is composed (`AgentToolExecutorModel`
    // → `withAnswerLanguageNote`).
    executor.setAnswerLanguageProvider(() =>
      this.workflowRunModel.isRunning ? this.workflowRunModel.translationLanguage : null,
    );

    executor.attach(this.toolsModel, [
      new EntitiesExecutor(
        deps.project,
        deps.selection,
        deps.placement,
        deps.explosion,
        deps.undoRedo,
        host,
        this.guardrailsModel,
        () => this.workflowRunModel.describeCurrentStepAnimation(),
      ),
      new ContextExecutor(this.contextEngineModel, host),
      new DocsExecutor(this.contextEngineModel, host),
      new MemoryExecutor(deps.transcript, deps.auth),
      new ProjectExecutor(deps.projectList, deps.project),
      new AIBehaviourExecutor(host, this.guardrailsModel, deps.undoRedo, this.interfaceModel),
      new ScenesExecutor(deps.project, this.workflowRunModel),
      new SelectionSetsExecutor(deps.project, host),
      new AssetsExecutor(deps.project, deps.materials, deps.selection),
      new WorkflowExecutor(
        deps.workflow,
        this.workflowScriptRunner,
        deps.prompts,
        this.workflowRunModel,
        this.guardrailsModel.stepMoves,
      ),
    ]);

    // --- Runtime wiring (Unity `StartRuntime`). ------------------------
    this.toolsModel.refreshToolAvailability(deps.project.activeProject !== null);
    // Role/skill tool gate (Unity `SetRoleSkillToolFilterProvider` in
    // `InitializeRealtime`): evaluated on every schema emit.
    this.toolsModel.setRoleSkillToolFilterProvider(() =>
      this.interfaceModel.getAllowedToolIdsForActiveRole(),
    );
    // The "@" role picker in the AI panel switches roles through the interface model, which
    // routes back here (keeps AI UI off `ai/orchestrator/*`; Unity's panel calls the orchestrator).
    this.interfaceModel.attachRoleController({
      switchRole: (roleId) => this.setAgentRole(roleId),
    });
    // Which documents state an answer (Spark Answer References, Phase 1). Its
    // `citation.*` notes ride the same trace log as every other row, and its
    // tokens count toward the session the way the other helper calls do.
    this.interfaceModel.attachAnswerAttribution(
      new AIAnswerAttributionModel({
        log: this.conversationLog,
        trackUsage: (usage) => this.realtimeSessionModel.addResponsesApiUsage("Answer Attribution", usage),
      }),
    );
    // Real file inputs exist only on the Responses transport — see
    // `AIFileInputController`. The panel asks through the interface model
    // rather than working out which transport is live.
    this.interfaceModel.attachFileInputController({
      supportsFileInput: () => this.isTextBridgeActive(),
      accept: RESPONSES_ATTACHMENT_ACCEPT,
      sendFileAsync: (file, text) => this.sendUserFileToBridgeAsync(file, text),
    });
    this.interfaceModel.setActiveRoleId(this.m_roleId);
    // Text turns reach the bridge through the session host (which also posts
    // the user's words into the realtime conversation, response-less, so the
    // voice model keeps context until `resetTextBridge` removes them); voice
    // turns are routed from the transcript subscription below — they never
    // pass through `sendUserText`.
    this.realtimeSessionModel.setExternalTurnRouter({
      tryRouteUserTurn: (text) => {
        if (!this.isTextBridgeActive()) return false;
        void this.m_textBridge.runUserTurnAsync(text);
        return true;
      },
      cancelActiveTurn: () => this.m_textBridge.cancelActiveTurn(),
    });
    this.m_offFns.push(
      // Capture only turns explicitly submitted by the user-facing facade;
      // INTERNAL SAY and tool-generated conversation items bypass this event.
      this.interfaceModel.onUserTextSubmitted.on((text) => {
        this.m_userTurnCount++;
        this.clearAgentSceneVisualsForNewTurn();
        this.recordTranscriptUserMessage(text, "text");
        this.guardrailsModel.stepMoves.noteLearnerText(text);
      }),
      // The spoken half of the step-move guardrail's learner turns, by item id.
      // Not the transcript subscription below: that one never sees an empty
      // transcript, and noise that became a turn is exactly what it must see.
      this.realtimeSessionModel.onUserVoiceTurn.on((event) => {
        const stepMoves = this.guardrailsModel.stepMoves;
        if (event.kind === "started") stepMoves.noteLearnerVoiceStarted(event.itemId);
        else if (event.kind === "delta") stepMoves.noteLearnerVoiceDelta(event.itemId, event.delta);
        else stepMoves.noteLearnerVoiceSettled(event.itemId, event.transcript);
      }),
      this.realtimeSessionModel.onAssistantTextCompleted.on(({ text, origin }) => {
        // Narration included, and so BEFORE the script return below: a step
        // that ends by asking "ready to move on?" is asking it as much as Spark
        // is, and a bare "yes" is the learner's answer to it.
        this.guardrailsModel.stepMoves.noteSparkLine(text);
        // ⚠ A workflow `say` is NEVER an answer, and never reaches the saved
        // transcript (user direction, 2026-09-23, AI-1395). The transcript
        // holds what the learner said and Spark's responses to it; a step's
        // narration is the step reading its own copy aloud — even when it
        // plays because the learner asked to go to that step. Without this,
        // every spoken step move saved the next step's instructions as the
        // reply to "let's go ahead", because the move itself says nothing and
        // the narration is the next text to arrive. The question stays
        // unanswered; the next one replaces it (`recordUserMessage`). The label
        // is the text's own — see `AssistantText`.
        if (origin === "script") return;
        try {
          deps.transcript.recordAssistantMessage(text);
        } catch (error) {
          console.warn("[AIOrchestrator] Failed to record transcript answer:", error);
        }
      }),
      // A tool whose outcome has ONE fixed confirmation (`reply="…"` — see
      // `toolTurnHandover.ts`) ended its turn without a model reply, and this
      // is that reply. One subscription covers both transports: the Responses
      // bridge re-publishes its tool completions through this same emitter.
      // Deferred a microtask because the realtime transport emits BEFORE it
      // posts the call's output, and a line sent first would land between the
      // call and its output.
      this.realtimeSessionModel.onToolExecutionCompleted.on(({ toolName, result }) => {
        const line = toolSpokenReply(toolName, result);
        if (line === null) return;
        // In the run's language, like the step narration — read NOW, because a
        // stop has already ended the run by the time its line is spoken, and
        // its "Workflow stopped" is still the last line of that run.
        const language =
          this.workflowRunModel.isRunning || toolName === AgentToolNames.StopWorkflow
            ? this.workflowRunModel.translationLanguage
            : null;
        const writtenIn = this.workflowRunModel.workflowLanguage;
        queueMicrotask(() => this.deliverToolSpokenReply(line, language, writtenIn));
      }),
      // Per-role session model (`roleModels` in the shared settings): the
      // settings document loading — or the debug panel's model/effort dropdown
      // moving — can change what the ACTIVE role's session should run on.
      // `onChanged` fires for every settings edit, so the policy applier
      // no-ops unless the resolved model config actually moved.
      // The transport switch lives in `devSettings`, so a WRITE to it already
      // fires `appSettings.onChanged` below — but the DEBUG GATE flips through
      // `IDebugModeModel` and never touches that document, while
      // `DevSettingsModel` re-emits both. Without this subscription, leaving
      // debug mode changes the effective transport while a live session stays
      // connected on the other one.
      //
      // ⚠ `false`, never `true`: this emitter fires for every unrelated dev
      // toggle, and `alwaysRefresh: true` would push a ~30 KB `session.update`
      // for a Super Gizmo flip.
      // Bounds `m_modelFallbackJustFired`: a model fallback that is never
      // followed by a transport code must not suppress the NEXT genuine one.
      this.realtimeSessionModel.onStatusChanged.on((status) => {
        if (status === "connected") this.m_modelFallbackJustFired = false;
      }),
      deps.devSettings.onChanged.on(() => this.applySessionModelPolicy(false)),
      deps.appSettings.onChanged.on(() => this.applySessionModelPolicy(false)),
      // Endpoint rejections of the configured model/effort — see the two
      // rejected-set fields above. Without this, a stored model the endpoint
      // refuses loops Spark offline forever (reconnect retries the same URL).
      this.realtimeSessionModel.onErrorOccurred.on((code) =>
        this.handleSessionErrorForModelPolicy(code),
      ),
      // With the bridge active, server VAD transcribes but no longer
      // auto-answers (`create_response: false`) — this subscription is the
      // voice turn's only responder. Text turns route via the turn router.
      this.realtimeSessionModel.onUserTranscriptCompleted.on((transcript) => {
        // An empty transcript is mic noise, not the learner taking a turn.
        if (transcript.trim().length > 0) {
          this.m_userTurnCount++;
          this.clearAgentSceneVisualsForNewTurn();
        }
        this.recordTranscriptUserMessage(transcript, "voice");
        if (this.isTextBridgeActive()) void this.m_textBridge.runUserTurnAsync(transcript);
      }),
      // Listening is project-scoped. Close the microphone at the start of
      // every shared unload path and persist the off state, so neither constant
      // listening nor a one-shot window follows the user to the projects list
      // or into the next project.
      deps.project.onBeginUnloadProject.on(() => {
        this.interfaceModel.setConstantListeningEnabled(false);
      }),
      deps.project.onActiveProjectChanged.on((project) => {
        this.toolsModel.refreshToolAvailability(project !== null);
        // A project switch is a new conversation context for the bridge too.
        this.resetTextBridge();
        deps.transcript.resetConversation();
        // Nothing said in the last project asks for a move in this one.
        this.guardrailsModel.stepMoves.reset();
        // Opened before the role reset: that reset refreshes the session too,
        // and on entry it describes a project whose contents have not arrived.
        if (project) this.beginProjectEntrySettling();
        else this.cancelProjectEntrySettling();
        // The role is session state, so a project switch — either way — starts
        // over on the default role rather than carrying the previous project's
        // pick into the next one.
        this.resetRoleForProjectChange();
        void this.refreshProjectContextWithAnswerSkillAsync(project?.id ?? null).catch((error) => {
          console.warn("[AIOrchestratorModel] Answer skill preload failed:", error);
          this.cancelProjectEntrySettling();
          this.realtimeSessionModel.refreshSessionContext();
        });
        // Unity `OnProjectUpdated`: gaze at the model root + refresh
        // Spark visibility for the new project state.
        this.setSparkGazeToModelRoot();
        this.updateSparkVisibilityForSceneState();
      }),
      deps.workflow.onWorkflowChanged.on((event) => {
        void this.syncWorkflowDocumentsForSearchAsync(event, "background");
      }),
      // Playback owns the role while it lasts (see `applyWorkflowAgentRole`).
      this.workflowRunModel.onRunStateChanged.on(() => this.handleWorkflowRunStateChanged()),
      // …and a move the LEARNER makes is said in the conversation too,
      this.workflowRunModel.onStepArrived.on((arrival) => this.handleWorkflowStepArrived(arrival)),
      // …as is a run starting, which is not an arrival.
      this.workflowRunModel.onRunStarted.on((start) => this.handleWorkflowRunStarted(start)),
      deps.project.onActiveSceneChanged.on(() => {
        // One half of project entry; the project context is the other. Whichever
        // lands last sends the single `session.update` that describes both.
        this.refreshSessionContextOrHold();
        this.noteProjectEntrySceneReady();
        // Unity `OnActiveSceneChanged`: look at the user on scene change.
        this.m_sparkBehavior?.lookAtUser();
        this.updateSparkVisibilityForSceneState();
      }),
      // Unity parity: executor mutations fire `ContextInvalidated`, which
      // invalidates the cache AND refreshes the live session instructions
      // (`RefreshCurrentSessionContext("Context invalidated")`). Passive
      // project-event invalidation inside the context engine deliberately
      // does NOT push a session.update — the per-user-turn refresh in
      // `AIRealtimeSessionModel.sendUserText` covers it without spamming
      // ~30 KB instruction payloads on every reconciliation event.
      executor.onContextInvalidated.on(() => {
        this.contextEngineModel.invalidateCache();
        this.refreshSessionContextOrHold();
      }),
      // --- Spark gaze routing (Unity `AIOrchestratorModel.cs`) ---------
      // Selection → look at the selected part; cleared → model root.
      deps.selection.onChanged.on((change) => {
        if (!this.m_sparkBehavior) return;
        if (change.primaryId && change.selectedIds.size > 0) {
          const node = this.resolveSparkNodeForSnapshotId(change.primaryId);
          if (node) this.m_sparkBehavior.setObjectOfInterest(node);
        } else if (change.selectedIds.size === 0) {
          this.setSparkGazeToModelRoot();
        }
      }),
      // Thinking → glance at the current selection. Unity does exactly this
      // inside `HandleThinkingStateChanged`, gated on the NARROW thinking flag:
      // the glance belongs to the moment a turn starts, not to the whole turn.
      // A spoken turn is answered against the instructions already on the
      // session and never refreshes them, so an arrival's held refresh goes the
      // moment the learner starts talking — ahead of the reply server VAD will
      // ask for when they stop.
      this.realtimeSessionModel.onUserVoiceStarted.on(() => this.releaseNarrationRefreshHold()),
      this.realtimeSessionModel.onThinkingStateChanged.on((thinking) => {
        if (!thinking || !this.m_sparkBehavior) return;
        const primary = deps.selection.primaryId;
        const node = primary ? this.resolveSparkNodeForSnapshotId(primary) : null;
        if (node) this.m_sparkBehavior.setObjectOfInterest(node);
      }),
      // The thinking crown follows the CONSOLIDATED activity state instead —
      // Unity's `UpdateActivityState` assigns
      // `m_sparkBehavior.isThinking = m_aiInterfaceModel.IsActivityActive`,
      // i.e. thinking OR a tool running OR the agent speaking OR an open
      // response. Driving it from `onThinkingStateChanged` dropped the crown
      // mid-sentence, because audio playback outlives the final thinking
      // callback — the case Unity's own `OnAgentSpeakingStateChanged` comment
      // calls out. Deliberately NOT the panel ring's predicate, which also ORs
      // in `isListening`: Spark wears the crown while it works, not while it
      // listens to the user.
      this.interfaceModel.onActivityStateChanged.on((active) => {
        this.m_sparkBehavior?.setThinking(active);
      }),
      // Conversation outranks ambient scene attention. This uses the narrow
      // speaking signal rather than consolidated activity so thinking/tools
      // may keep their purposeful part target until audio actually begins.
      this.interfaceModel.onAgentSpeakingChanged.on((speaking) => {
        this.m_sparkBehavior?.setAgentSpeaking(speaking);
      }),
      // Response completed → look back at the user (`OnResponseCompleted`),
      // unless a look_at tool pinned the focus target.
      this.realtimeSessionModel.onAssistantTextCompleted.on(() => {
        const keepFocus = this.m_keepSparkFocusOnToolTargetAfterResponse;
        this.m_keepSparkFocusOnToolTargetAfterResponse = false;
        if (!this.m_sparkBehavior) return;
        if (!keepFocus) this.m_sparkBehavior.lookAtUser();
        this.m_sparkBehavior.setInteracting(false);
      }),
      // User speech → follow the camera (`CheckSpeakingParticipants`).
      this.realtimeSessionModel.onUserVoiceStarted.on(() =>
        this.m_sparkBehavior?.setUserSpeaking(true),
      ),
      this.realtimeSessionModel.onUserVoiceStopped.on(() =>
        this.m_sparkBehavior?.setUserSpeaking(false),
      ),
      // Spark's OWN changes during a guided run last only until the step
      // changes (AI-1424): the runner captures the pre-tool state here and puts
      // it back on the next step move. The start event fires after the
      // guardrail allowed the call and before it is dispatched — the last moment
      // that state is still readable. A workflow script's calls are captured by
      // the runner itself and are skipped here; the completion half tells the
      // runner when none of Spark's calls is still running.
      executor.onToolExecutionStarted.on(({ toolName, args, triggeredBy }) => {
        if (triggeredBy === TOOL_TRIGGER.WORKFLOW) return;
        this.workflowScriptRunner.beginLiveToolCall(toolName, args);
      }),
      executor.onToolExecutionCompleted.on(({ toolName, triggeredBy }) => {
        if (triggeredBy === TOOL_TRIGGER.WORKFLOW) return;
        this.workflowScriptRunner.endLiveToolCall(toolName);
      }),
      // Tool acts on a part → look at it (`NotifySparkLookAtPart`, driven
      // by Core_Executor's `ExtractFirstPartFromArgs` in Unity).
      //
      // Deliberately NOT filtered on `triggeredBy`: an authored workflow script
      // command operates on a part exactly as the agent's own call does, and
      // the learner watching a guided step is the reader who most needs to be
      // shown WHICH part it is. Both triggers therefore get the same glance —
      // which is only safe because it now expires (`K_SPARK_TOOL_GAZE_HOLD_S`);
      // a script never fires the response-completed release.
      executor.onToolExecutionStarted.on(({ toolName, args }) => {
        this.notifySparkLookAtPartFromArgs(toolName, args);
      }),
      executor.onToolExecutionCompleted.on(() => {
        this.m_sparkBehavior?.setInteracting(false);
      }),
    );
  }

  // -- Spark behaviour (ISparkBehaviorHost) --------------------------------

  registerSparkBehavior(spark: ISparkBehavior): void {
    if (this.m_sparkBehavior === spark) {
      this.updateSparkVisibilityForSceneState();
      return;
    }
    this.m_sparkBehavior = spark;
    this.setSparkGazeToModelRoot();
    // `onActivityStateChanged` only fires on transitions, and the viewport can
    // mount mid-turn, so seed the crown from the live state rather than leaving
    // it dark until the next one.
    spark.setThinking(this.interfaceModel.isActivityActive);
    spark.setAgentSpeaking(this.interfaceModel.isAgentSpeaking);
    this.updateSparkVisibilityForSceneState();
  }

  unregisterSparkBehavior(spark: ISparkBehavior): void {
    if (this.m_sparkBehavior === spark) this.m_sparkBehavior = null;
  }

  /** `Toggle_Spark` entry point (Unity `SetSparkVisibility`). */
  setSparkVisibility(visible: boolean): boolean {
    this.m_sparkHiddenByUser = !visible;
    this.updateSparkVisibilityForSceneState();
    return this.m_sparkBehavior !== null;
  }

  private updateSparkVisibilityForSceneState(): void {
    if (!this.m_sparkBehavior) return;
    const sceneLoaded =
      this.m_deps.project.activeProject !== null &&
      this.m_deps.project.activeSceneId !== null;
    if (this.m_sparkHiddenByUser || !sceneLoaded) {
      this.m_sparkBehavior.hideSpark();
    } else {
      this.m_sparkBehavior.showSpark();
    }
  }

  /** Unity `SetSparkGazeToModelRoot`: look at the first root model. */
  private setSparkGazeToModelRoot(): void {
    if (!this.m_sparkBehavior) return;
    const sceneId = this.m_deps.project.activeSceneId;
    const entities = sceneId ? this.m_deps.project.getEntities(sceneId) : [];
    for (const entity of entities) {
      const root = entity.snapshots.find((s) => s.isRoot) ?? entity.snapshots[0];
      if (!root) continue;
      const node = this.resolveSparkNodeForSnapshotId(root.id);
      if (node) {
        this.m_sparkBehavior.setObjectOfInterest(node);
        return;
      }
    }
    this.m_sparkBehavior.lookAtUser();
  }

  /**
   * Port of Core_Executor's `ExtractFirstPartFromArgs` + Unity's
   * `NotifySparkLookAtPart`: peek at the tool args for hierarchy paths and
   * point Spark at the first resolvable target, for
   * {@link K_SPARK_TOOL_GAZE_HOLD_S} seconds.
   *
   * Runs for every trigger — the agent's own calls and authored workflow script
   * commands alike. A tool carrying no part path (`clear_isolation`) leaves the
   * gaze alone rather than releasing it: there is nothing to look at, which is
   * not the same as a reason to look away.
   */
  private notifySparkLookAtPartFromArgs(toolName: string, args: ToolArgs): void {
    if (!this.m_sparkBehavior) return;
    const paths: string[] = [];
    for (const key of K_SPARK_PART_PATH_ARG_KEYS) {
      paths.push(...getStringList(args, key));
      if (paths.length > 0) break;
    }
    if (paths.length === 0) {
      const singular = getString(args, "partPath");
      if (singular) paths.push(singular);
    }
    if (paths.length === 0) {
      this.notifySparkLookAtTextBoxFromArgs(toolName, args);
      return;
    }

    const sceneId = this.m_deps.project.activeSceneId;
    if (!sceneId) return;
    const entries = resolveManyByPath(this.m_deps.project.getEntities(sceneId), paths);
    // The FIRST path that resolves wins — several parts cannot be looked at at
    // once, and the first is the one the caller named first.
    for (const entry of entries) {
      const node = this.resolveSparkNodeForSnapshotId(entry.snapshot.id);
      if (!node) continue;
      // `look_at` exists to PIN the gaze, so it alone keeps the open-ended
      // Unity behaviour and is released by the response instead.
      const pin = toolName === AgentToolNames.LookAtPart;
      this.m_sparkBehavior.setObjectOfInterest(
        node,
        pin ? undefined : K_SPARK_TOOL_GAZE_HOLD_S,
      );
      this.m_sparkBehavior.setInteracting(true);
      this.m_keepSparkFocusOnToolTargetAfterResponse = pin;
      return;
    }
  }

  /**
   * `look_at` on a card: Unity's `LookAtPart` notifies Spark with its first
   * target itself, whatever it is, so a card is looked at as a part would be —
   * and pinned, as `look_at` always is. Only `look_at`: the generic scan above
   * is `ExtractFirstPartFromArgs`, which reads no card argument upstream.
   */
  private notifySparkLookAtTextBoxFromArgs(toolName: string, args: ToolArgs): void {
    if (!this.m_sparkBehavior || toolName !== AgentToolNames.LookAtPart) return;
    const sceneId = this.m_deps.project.activeSceneId;
    if (!sceneId) return;
    const tiles = this.m_deps.project.getMediaTiles(sceneId);
    for (const name of getStringList(args, "textBoxNamesToLookAt")) {
      const wanted = name.trim().toLowerCase();
      const tile = tiles.find((candidate) => candidate.name.trim().toLowerCase() === wanted);
      const node = tile ? this.resolveSparkNodeForSnapshotId(tile.id) : null;
      if (!node) continue;
      this.m_sparkBehavior.setObjectOfInterest(node, undefined);
      this.m_sparkBehavior.setInteracting(true);
      this.m_keepSparkFocusOnToolTargetAfterResponse = true;
      return;
    }
  }

  private resolveSparkNodeForSnapshotId(snapshotId: string) {
    if (!Ioc.isRegistered<PickingService>(PICKING_SERVICE)) return null;
    return Ioc.resolve<PickingService>(PICKING_SERVICE).resolveSnapshotNode(snapshotId);
  }

  // -- Session policy (IRealtimeSessionPolicyProvider) -------------------

  buildSessionOptions(): RealtimeSessionOptions {
    return {
      instructions: this.buildMainAgentInstructionsWithProjectContext(),
      voiceName: this.currentVoiceName,
      enableAudioOutput: true,
      enableInputAudioTranscription: true,
      transcriptionLanguage: this.m_transcriptionLanguageRejected
        ? ""
        : getVoiceSettings().transcription.language,
      // While the bridge answers this role's turns, the realtime model must
      // not answer them too — it keeps transcribing and speaking INTERNAL SAY
      // lines only.
      autoRespondToUserAudio: !this.isTextBridgeActive(),
    };
  }

  /**
   * Whether the active role's turns run through the Responses text bridge:
   * its configured model is a non-realtime one (which the websocket refuses at
   * connect), and the direct Campfire proxy can authenticate `/v1/responses`
   * with the signed-in user's Cognito token.
   */
  /**
   * Uploads a picked file and sends it as ONE bridged user turn, carrying the
   * message the user typed with it.
   *
   * `text` reaches `runUserTurnAsync` rather than being sent as a turn of its
   * own: the bridge already takes both, and two turns would ask the model to
   * answer the document before the question arrived. Empty `text` keeps the
   * bare-attachment wording the bridge writes for itself.
   *
   * The realtime conversation gets a response-less note that a file was handed
   * over — with the user's words when there are any — so a later voice turn on
   * the same session still has the context. That is the same reason a bridged
   * TEXT turn posts the user's words there.
   */
  private async sendUserFileToBridgeAsync(
    file: File,
    text: string,
  ): Promise<AIFileInputResult> {
    if (!this.isTextBridgeActive()) {
      return { ok: false, reason: "Files are not supported for this role." };
    }
    let attachment;
    try {
      attachment = await this.m_fileInput.buildAttachmentAsync(file);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      this.conversationLog.note("bridge.attachment", `failed: ${reason}`);
      return { ok: false, reason };
    }
    const message = text?.trim() ?? "";
    const note = `[Attached file "${file.name}"]`;
    this.realtimeSessionModel.postUserContextText(message ? `${note} ${message}` : note);
    void this.m_textBridge.runUserTurnAsync(message, [attachment]);
    return { ok: true };
  }

  /**
   * Drops the bridged conversation, its turns' copies in the realtime
   * conversation, AND the objects it uploaded — a `file_id` outlives nothing
   * but the history that references it, so the two must reset together or the
   * account accumulates orphans.
   *
   * The realtime copies are the user's words, posted response-less so the
   * voice model has the context while the bridge holds the role. With the
   * role's reply text-only (`workflow-builder` ships so), nothing in that
   * conversation ever answered them, and the next response it was asked for
   * did: Play after a builder request opened the run with the Guide refusing
   * the request instead of saying the Welcome (AI-1460).
   */
  private resetTextBridge(): void {
    this.m_textBridge.reset();
    this.realtimeSessionModel.discardRoutedTurns();
    void this.m_fileInput.releaseAllAsync();
  }

  /**
   * Speaks a tool's FIXED confirmation in the model's place — see
   * `toolSpokenReply` in `toolTurnHandover.ts` for which tools have one and
   * why.
   *
   * The same two surfaces a bridged role's own replies use, chosen by the role
   * the line LANDS in rather than the one that made the call: a stop has
   * already handed the session back by the time this runs. A realtime role, or
   * a bridged one with voice on, gets it spoken and shown through
   * `INTERNAL SAY:`, which the base prompt has the model speak word for word; a
   * bridged role with voice off gets the text published as its answer, with no
   * model turn at all.
   *
   * "Voice off" is EITHER gate: the role's own `RoleModelConfig.voice`
   * (`workflow-builder` ships off) or the debug panel's global override. Both
   * are read HERE rather than captured at the call, which is what keeps the
   * "role the line lands in" contract true across a stop that switched roles.
   */
  private deliverToolSpokenReply(text: string, language: string | null, writtenIn: string): void {
    const withVoice =
      !this.isTextBridgeActive() ||
      (this.m_deps.appSettings.getRoleModel(this.m_roleId).voice &&
        this.m_deps.devSettings.isResponsesVoiceEnabled);
    this.conversationLog.note("tool.reply", withVoice ? "mode=voice" : "mode=text");
    if (withVoice) {
      this.realtimeSessionModel.sendUserText(formatInternalSay(text, language, writtenIn), "text");
    } else {
      this.realtimeSessionModel.notifyExternalAssistantText(text);
    }
  }

  private isTextBridgeActive(): boolean {
    const model = this.m_deps.appSettings.getRoleModel(this.m_roleId).model;
    return model !== null && !isRealtimeFamilyModel(model);
  }

  private recordTranscriptUserMessage(text: string, inputType: AITranscriptInputType): void {
    const context = this.buildTranscriptContext();
    if (!context) return;
    try {
      this.m_deps.transcript.recordUserMessage(text, inputType, context);
    } catch (error) {
      // Transcript persistence is observational and must never interrupt a turn.
      console.warn("[AIOrchestrator] Failed to record transcript question:", error);
    }
  }

  private recordRealtimeLogEvent(event: RealtimeLogRecordedEvent): void {
    const context = this.buildTranscriptContext();
    if (!context) return;
    try {
      this.m_deps.transcript.recordRealtimeLogEvent(event, context);
    } catch (error) {
      // Transcript persistence is observational and must never interrupt a turn.
      console.warn("[AIOrchestrator] Failed to record realtime log event:", error);
    }
  }

  private buildTranscriptContext(): AITranscriptContext | null {
    const user = this.m_deps.auth.currentUser;
    const project = this.m_deps.project.activeProject;
    if (!user?.customerId || !user.userId || !project) return null;

    const workflow = this.m_deps.workflow.loadedWorkflow;
    const playback = this.workflowRunModel.getPlaybackView();
    const step = workflow && playback ? workflow.findStep(playback.stepName) : null;
    const configuredModel = this.m_deps.appSettings.getRoleModel(this.m_roleId).model;
    const modelId = this.isTextBridgeActive()
      ? configuredModel ?? getVoiceSettings().realtime.model
      : this.realtimeSessionModel.connectedModel ?? this.resolveSessionConnection().model;

    return {
      customerId: user.customerId,
      projectId: project.id,
      projectCustomerId: project.customerId?.trim() || user.customerId,
      userId: user.userId,
      userDisplayName: user.displayName,
      workflowId: workflow?.uid.trim() || null,
      stepId: step?.stepId.trim() || null,
      stepName: step?.name.trim() || null,
      sceneId: this.m_deps.project.activeSceneId,
      agentRoleId: this.m_roleId,
      modelId,
      realtimeSessionId: this.realtimeSessionModel.sessionId,
      userInteractionState: this.contextEngineModel.captureUserInteractionState(),
    };
  }

  buildTurnUserContext(): string | null {
    return this.contextEngineModel.tryBuildUserContextForMessage();
  }

  /**
   * The model + reasoning effort the realtime session should run on for the
   * ACTIVE role — the account's `roleModels` config (shipped default:
   * `workflow-builder` → gpt-5.6-sol at medium effort), falling back to the
   * stock realtime model. Read by the transport at connect time (the model
   * rides the websocket URL) and by the session payload (`reasoning.effort`).
   */
  resolveSessionConnection(): RealtimeSessionConnectionConfig {
    const config = this.m_deps.appSettings.getRoleModel(this.m_roleId);
    const stockModel = getVoiceSettings().realtime.model;
    let model = config.model ?? stockModel;
    let reasoningEffort = config.reasoningEffort;
    // OPT-OUT and debug-gated (see `IDevSettingsModel`) — WebRTC is the
    // shipped default — then narrowed by what this app session has already
    // found to be unreachable.
    let transport: RealtimeTransportKind = this.m_deps.devSettings
      .isWebRtcRealtimeTransportEnabled
      ? "webrtc"
      : "websocket";
    if (this.m_rejectedTransports.has(transport)) transport = "websocket";
    if (!isRealtimeFamilyModel(model)) {
      // A non-realtime model cannot ride the websocket (refused at connect —
      // measured): the ROLE runs it through the text bridge, and the SOCKET
      // stays on the stock voice config.
      model = stockModel;
      reasoningEffort = null;
    }
    // Voice roles take the shipped realtime default when the account has
    // configured nothing; an explicit stored effort always wins.
    reasoningEffort = resolveRealtimeReasoningEffort(model, reasoningEffort);
    if (this.m_rejectedSessionModels.has(model)) {
      // The endpoint refused this model at connect: fall the WHOLE config back
      // (the stock model may not accept the configured effort either).
      model = stockModel;
      reasoningEffort = null;
    }
    if (reasoningEffort !== null && this.m_effortRejectedModels.has(model)) {
      reasoningEffort = null;
    }
    return { model, reasoningEffort, transport };
  }

  /**
   * Self-heals the two ways the live endpoint can refuse the configured
   * session-model policy (both measured against the dev proxy, 2026-08-13):
   * a non-realtime model is refused at CONNECT with `invalid_model` and the
   * server closes the socket — retrying the same URL loops Spark offline —
   * and a `reasoning.effort` key is refused on `session.update` with
   * `invalid_value`, which voids the whole update so instructions and tools
   * never land. Either way the session must end up running, on the closest
   * config the endpoint accepts, with the downgrade logged rather than silent.
   */
  private handleSessionErrorForModelPolicy(code: string): void {
    const resolved = this.resolveSessionConnection();
    const stockModel = getVoiceSettings().realtime.model;

    if (code === "invalid_model" && resolved.model !== stockModel) {
      this.m_rejectedSessionModels.add(resolved.model);
      console.warn(
        `[AIOrchestrator] realtime endpoint refused model "${resolved.model}" — ` +
          `falling back to ${stockModel} for this app session`,
      );
      this.conversationLog.note(
        "session.model-fallback",
        `${resolved.model} → ${stockModel} (invalid_model)`,
      );
      this.m_modelFallbackJustFired = true;
      void this.realtimeSessionModel.restartAsync("model-rejected-fallback");
      return;
    }

    if (K_TRANSPORT_FAILURE_CODES.has(code) && this.m_modelFallbackJustFired) {
      // The model self-heal owns this failure and has already restarted.
      this.m_modelFallbackJustFired = false;
      return;
    }

    if (
      K_WEBSOCKET_FALLBACK_FAILURE_CODES.has(code)
      && resolved.transport === "webrtc"
    ) {
      this.m_rejectedTransports.add("webrtc");
      console.warn(
        `[AIOrchestrator] WebRTC connectivity failed (${code}) — ` +
          "falling back to websocket for this app session",
      );
      this.conversationLog.note(
        "session.transport-fallback",
        `webrtc → websocket (${code})`,
      );
      void this.realtimeSessionModel.restartAsync("transport-connectivity-fallback");
      return;
    }

    if (
      (code === "invalid_value" || code === "unknown_parameter") &&
      resolved.reasoningEffort !== null
    ) {
      this.m_effortRejectedModels.add(resolved.model);
      console.warn(
        `[AIOrchestrator] model "${resolved.model}" refused reasoning.effort — ` +
          `resending the session config without it`,
      );
      this.conversationLog.note(
        "session.effort-fallback",
        `${resolved.model} (reasoning.effort dropped)`,
      );
      this.realtimeSessionModel.refreshSessionContext();
      return;
    }

    // The transcription language is the only other key we add to
    // `session.update`, so a refusal that survives the effort branch above is
    // its. Ordering matters and degrades in sequence: while an effort is still
    // live that branch owns `invalid_value` and retries without it; only once
    // the model is in `m_effortRejectedModels` does the same refusal arrive
    // here.
    //
    // ⚠ THIS LATCH IS SHARED BY EVERY TRANSCRIPTION KEY WE EVER ADD, and it is
    // app-session-sticky with no reset. A probe of a NEW key (`keywords`,
    // `languages`, `delay`) that the endpoint does not recognise therefore
    // silently un-pins ENGLISH too — regressing the code-switching bug the pin
    // exists for, invisibly, for the rest of the session. Probe on a throwaway
    // session, never a demo build, and split this latch per key first.
    if (
      (code === "invalid_value" || code === "unknown_parameter")
      && !this.m_transcriptionLanguageRejected
    ) {
      this.m_transcriptionLanguageRejected = true;
      console.warn(
        "[AIOrchestrator] realtime endpoint refused the transcription language — " +
          "resending the session config without it",
      );
      this.conversationLog.note(
        "session.transcription-fallback",
        "language dropped",
      );
      this.realtimeSessionModel.refreshSessionContext();
    }
  }


  /**
   * Brings the live session in line with {@link resolveSessionConnection}. The model
   * is part of the websocket URL, so a MODEL change needs a reconnect (the
   * personality-restart precedent — the conversation does not survive, which is
   * inherent to switching models); an EFFORT-only change rides `session.update`.
   *
   * `alwaysRefresh` distinguishes the two callers: a role switch must refresh
   * the instructions even when the model config did not move, while the shared
   * settings' `onChanged` must not push a ~30 KB `session.update` for every
   * unrelated toggle.
   */
  private applySessionModelPolicy(alwaysRefresh: boolean): void {
    const resolved = this.resolveSessionConnection();
    const key = `${resolved.model}|${resolved.reasoningEffort ?? ""}`;
    const keyChanged = key !== this.m_sessionModelKey;
    this.m_sessionModelKey = key;

    const connected = this.realtimeSessionModel.connectedModel;
    const connectedTransport = this.realtimeSessionModel.connectedTransport;
    // The restart is deliberately NOT gated on `keyChanged`: that key exists to
    // decide whether a `session.update` is worth sending, and neither the model
    // nor the transport can ride one. Both are fixed for a connection's life.
    if (
      connected !== null &&
      (connected !== resolved.model || connectedTransport !== resolved.transport)
    ) {
      void this.realtimeSessionModel.restartAsync(
        connected !== resolved.model ? "session-model-change" : "session-transport-change",
      );
      return;
    }
    if (alwaysRefresh || keyChanged) this.refreshSessionContextOrHold();
  }

  // -- Prompts / personality ---------------------------------------------

  get currentPersonalityId(): string {
    return this.m_personalityId;
  }

  get currentVoiceName(): string {
    return getPersonality(this.m_personalityId).voiceName;
  }

  get currentRole(): string {
    return this.m_roleId;
  }

  /**
   * The ONLY role switch (Unity `SetAgentRole`): updates persona + skill/tool gating and
   * refreshes the live session. Session-scoped — nothing about a role selection is stored,
   * so the next project open (or reload) starts on the default role again.
   */
  setAgentRole(roleId: string): void {
    // Resolved against the account's role gate, not just normalized: the caller can
    // name a role the account has since withdrawn. Asking the interface model
    // keeps `m_roleId` in agreement with the role the session actually runs, so
    // the "@" token and the persona cannot disagree about which role is live.
    const normalized = this.interfaceModel.resolveEnabledRoleId(roleId);
    if (this.m_roleId.toLowerCase() === normalized) return;

    this.m_roleId = normalized;
    this.interfaceModel.setActiveRoleId(normalized);
    // `setActiveRoleId` prunes Answer on the way INTO a role outside its scope
    // (the Workflow Builder), so restore it on the way back out — see
    // `tryPreloadProjectAnswerSkill`. No refresh of its own: the policy call
    // below sends the one `session.update` this switch needs.
    this.tryPreloadProjectAnswerSkill(this.m_deps.project.activeProject !== null);
    // A new role is a new conversation for the bridge (its history carries the
    // previous role's persona and tool grants).
    this.resetTextBridge();
    // A role switch refreshes the session context — which also flips the
    // VAD auto-answer flag when the bridge takes over or hands back — and,
    // when the new role's configured model is a REALTIME-family one differing
    // from the connected socket, reconnects on it. Voice/personality still
    // never reconnects on a bare role switch.
    this.applySessionModelPolicy(true);
    this.onCurrentRoleChanged.emit(normalized);
  }

  /** Resets to the Assistant role whenever the active project changes (open or close). */
  private resetRoleForProjectChange(): void {
    this.m_roleBeforeWorkflow = null;
    this.setAgentRole(DEFAULT_ROLE_ID);
    // A workflow surface still open across the switch owns the new project's
    // session too, so re-assert instead of leaving an open editor on the default.
    this.applyWorkflowAgentRole();
  }

  /**
   * Waits for the catalogs when project loading wins the boot race, then sends
   * one refresh containing both the project context and Answer guidance.
   */
  private async refreshProjectContextWithAnswerSkillAsync(projectId: string | null): Promise<void> {
    if (projectId) await this.interfaceModel.ensureContentLoadedAsync();
    if ((this.m_deps.project.activeProject?.id ?? null) !== projectId) return;
    this.tryPreloadProjectAnswerSkill(projectId !== null);
    this.refreshSessionContextOrHold();
    this.noteProjectEntryContextReady();
  }

  /**
   * Project entry invalidates the context more than once: the project arrives
   * (and the Answer skill is preloaded into the same prompt), then the active
   * scene becomes ready. Each invalidation would push its own `session.update`,
   * and the ones before the scene lands describe a project whose contents are
   * not there yet, so the provider caches a prefix that is replaced moments
   * later. Hold them and send one update once the scene is in place.
   *
   * Only context invalidation is held. A user turn refreshes through
   * `AIRealtimeSessionModel.sendUserText`, which does not come through here, so
   * a question asked mid-entry still configures the session before it goes.
   */
  private beginProjectEntrySettling(): void {
    this.m_isProjectEntrySettling = true;
    this.m_hasHeldProjectEntryRefresh = false;
    this.m_isProjectEntryContextReady = false;
    this.m_isProjectEntrySceneReady = false;
    this.clearProjectEntrySettleTimer();
  }

  /**
   * The project context (and the Answer preload it waits on) is in place. The
   * scene can land either side of this — `ensureContentLoadedAsync` makes the
   * order a race — so both halves report in and the last one sends.
   */
  private noteProjectEntryContextReady(): void {
    if (!this.m_isProjectEntrySettling) return;
    this.m_isProjectEntryContextReady = true;
    if (this.m_isProjectEntrySceneReady) this.endProjectEntrySettling();
    else this.armProjectEntrySettleTimeout();
  }

  /** The project's first scene is active; see `noteProjectEntryContextReady`. */
  private noteProjectEntrySceneReady(): void {
    if (!this.m_isProjectEntrySettling) return;
    this.m_isProjectEntrySceneReady = true;
    if (this.m_isProjectEntryContextReady) this.endProjectEntrySettling();
  }

  /** Refreshes the live session, or records that it still owes one. */
  private refreshSessionContextOrHold(): void {
    if (this.m_isProjectEntrySettling) this.m_hasHeldProjectEntryRefresh = true;
    else if (this.m_isNarrationRefreshHeld) this.m_hasHeldNarrationRefresh = true;
    else this.realtimeSessionModel.refreshSessionContext();
  }

  /**
   * **A step's arrival holds context refreshes until its script first acts,
   * so the opening narration queues behind ONE `session.update`, not two.**
   *
   * A step move changes the prompt twice — the run bullets at once, the scene
   * context when the new scene activates — and each change used to push a
   * ~137 KB `session.update` on the spot. The server applies events in order
   * and takes ~0.6–0.8 s over each (measured 2026-09-30), so the step's first
   * `say` waited behind every update sent before it. With the next step's
   * scene prefetched (`WorkflowRunModel.prefetchNextStepScene`) both changes
   * land within milliseconds of Next, which would put BOTH ahead of the say.
   * Held, they leave as one update, sent immediately before the say.
   *
   * ⚠ Before the say, never after. Sending it after the say's
   * `response.create` started the narration ~0.5 s sooner, but voiced the line
   * on the PREVIOUS step's instructions, and on a step with trigger buttons the
   * Guide replaced the author's question with its own "press one of the
   * buttons" line (2 of 2 runs, traces of 2026-10-01). Do not reorder it
   * without an eval over trigger steps.
   *
   * The instructions are what a learner's next turn is answered against, and a
   * SPOKEN turn never refreshes them (`Campfire_Web_AI.md`, "Only a TEXT turn
   * refreshes the instructions"), so the hold is released by the first thing
   * that could need them: the script's first host action (`releaseNarrationRefreshHold`
   * in the script host), the learner starting to speak, the run stopping or
   * moving on, and a {@link K_NARRATION_REFRESH_HOLD_MAX_MS} ceiling. Only
   * opened for a step whose `on_start` script holds the floor — a step with no
   * script refreshes immediately, as it always did.
   */
  private openNarrationRefreshHold(): void {
    this.m_isNarrationRefreshHeld = true;
    this.clearNarrationRefreshHoldTimer();
    this.m_narrationRefreshHoldTimer = setTimeout(() => {
      this.m_narrationRefreshHoldTimer = null;
      this.releaseNarrationRefreshHold();
    }, K_NARRATION_REFRESH_HOLD_MAX_MS);
  }

  /** Ends the hold and sends the refresh it accumulated, if any. */
  private releaseNarrationRefreshHold(): void {
    if (!this.m_isNarrationRefreshHeld) return;
    this.m_isNarrationRefreshHeld = false;
    this.clearNarrationRefreshHoldTimer();
    if (!this.m_hasHeldNarrationRefresh) return;
    this.m_hasHeldNarrationRefresh = false;
    this.refreshSessionContextOrHold();
  }

  private clearNarrationRefreshHoldTimer(): void {
    if (this.m_narrationRefreshHoldTimer === null) return;
    clearTimeout(this.m_narrationRefreshHoldTimer);
    this.m_narrationRefreshHoldTimer = null;
  }

  /**
   * Opens the narration hold on an ARRIVAL at a step whose `on_start` script
   * holds the floor, and releases it once the run stops or that script lets go.
   * Called first thing on every run-state change, BEFORE the role switch and the
   * run-bullet refresh, so those are the refreshes it holds.
   */
  private syncNarrationRefreshHold(): void {
    const view = this.workflowRunModel.isRunning ? this.workflowRunModel.getPlaybackView() : null;
    const stepName = view?.stepName ?? null;
    if (stepName !== this.m_narrationHoldStepName) {
      this.m_narrationHoldStepName = stepName;
      if (view?.isOnStartScriptHoldingFloor) {
        this.openNarrationRefreshHold();
        return;
      }
    }
    if (!view?.isOnStartScriptHoldingFloor) this.releaseNarrationRefreshHold();
  }

  /**
   * Bounds the wait for the scene once the project itself has arrived. It is
   * deliberately not armed when the hold opens: loading a large project can take
   * far longer than this timeout, and there is nothing worth sending until it
   * finishes.
   */
  private armProjectEntrySettleTimeout(): void {
    if (!this.m_isProjectEntrySettling) return;
    this.clearProjectEntrySettleTimer();
    this.m_projectEntrySettleTimer = setTimeout(() => {
      this.m_projectEntrySettleTimer = null;
      this.endProjectEntrySettling();
    }, K_PROJECT_ENTRY_SETTLE_TIMEOUT_MS);
  }

  /** Releases the hold and sends the single update the entry accumulated. */
  private endProjectEntrySettling(): void {
    if (!this.m_isProjectEntrySettling) return;
    this.m_isProjectEntrySettling = false;
    this.clearProjectEntrySettleTimer();
    if (!this.m_hasHeldProjectEntryRefresh) return;
    this.m_hasHeldProjectEntryRefresh = false;
    this.realtimeSessionModel.refreshSessionContext();
  }

  /** Drops the hold without sending, for a project that went away mid-entry. */
  private cancelProjectEntrySettling(): void {
    this.m_isProjectEntrySettling = false;
    this.m_hasHeldProjectEntryRefresh = false;
    this.m_isProjectEntryContextReady = false;
    this.m_isProjectEntrySceneReady = false;
    this.clearProjectEntrySettleTimer();
  }

  private clearProjectEntrySettleTimer(): void {
    if (this.m_projectEntrySettleTimer === null) return;
    clearTimeout(this.m_projectEntrySettleTimer);
    this.m_projectEntrySettleTimer = null;
  }

  /**
   * Loads Answer without asking the agent to call `Load_Skill`. Never refreshes
   * the session itself — both callers send their own `session.update` straight
   * after, so the prompt they push already carries the skill.
   *
   * Runs on project open AND on every role switch. ⚠ The second one is
   * load-bearing, not a nicety: a role switch prunes every loaded skill outside
   * the new role's scope (Unity parity), and Answer is out of the Workflow
   * Builder's. So opening the editor and pressing Play — or opening and closing
   * it — dropped Answer for the rest of the project, and the Guide answered a
   * document question it had searched earlier straight from its own previous
   * reply, with no citation under it: the chips are published by the search,
   * and the skill carrying "search again, every time" was gone (2026-09-23,
   * trace `Spark_AI_tracing_2026-09-23_18-00`; the same question with Answer
   * loaded, four minutes earlier in the same conversation, searched again).
   *
   * A disabled or out-of-scope skill is left alone. The role check matters when
   * a workflow editor survives a project switch and immediately reclaims the
   * Workflow Builder role.
   */
  private tryPreloadProjectAnswerSkill(hasProject: boolean): void {
    if (!hasProject || this.interfaceModel.isSkillLoaded(K_PROJECT_DEFAULT_SKILL_ID)) return;
    const isInActiveRole = this.interfaceModel.availableSkillsForActiveRole.some(
      (skill) => skill.id.toLowerCase() === K_PROJECT_DEFAULT_SKILL_ID,
    );
    if (!isInActiveRole) return;
    this.interfaceModel.tryLoadSkill(K_PROJECT_DEFAULT_SKILL_ID, false);
  }

  /**
   * **The Workflow editor is authored in the Workflow Builder role.** The panel
   * has no prompt bar of its own — the author talks to the builder through the
   * normal Spark input — so opening it switches the session, and closing it
   * hands back whatever role opening it interrupted.
   */
  setWorkflowEditorOpen(open: boolean): void {
    if (this.m_isWorkflowEditorOpen === open) return;
    this.m_isWorkflowEditorOpen = open;
    this.applyWorkflowAgentRole();
  }

  /**
   * Two jobs, and only the first is gated on the run starting or stopping.
   *
   * The ROLE switch is: `onRunStateChanged` fires on every step move too, and
   * re-entering it would keep clearing the remembered role.
   *
   * The run-state BULLETS are not, because they change on every move, on every
   * completion gate that opens and on every composer hold that lifts — and a
   * VOICE turn never refreshes them. Server VAD auto-answers a spoken question
   * (`create_response: true` whenever the bridge is not carrying the role), so
   * nothing on that path reaches `AIRealtimeSessionModel.sendUserText`, which
   * is where Unity's per-user-turn `RefreshSessionContextForLatestUserTurn`
   * lives. A learner asking out loud was therefore answered against whatever
   * the bullets said at the last refresh — most visibly "Next is not available
   * on this step yet", minutes after the button went live.
   */
  private handleWorkflowRunStateChanged(): void {
    // FIRST: an arrival's hold has to be open before the role switch and the
    // bullet refresh below, which are the very updates it holds back.
    this.syncNarrationRefreshHold();
    const isPlaying = this.workflowRunModel.isRunning;
    const roleBeforeChange = this.m_roleId;
    if (isPlaying !== this.m_isWorkflowRunPlaying) {
      this.m_isWorkflowRunPlaying = isPlaying;
      this.applyWorkflowAgentRole();
    }

    // Signature-gated, the same shape `applySessionModelPolicy` and
    // `applyCoreToolSet` use: a `session.update` is a ~30 KB instruction
    // payload, and most of these emits do not move a single bullet.
    const runtimePrompt = this.workflowRunModel.buildRuntimePromptInstructions();
    if (runtimePrompt === this.m_sessionRuntimePrompt) return;
    this.m_sessionRuntimePrompt = runtimePrompt;
    // A role switch has ALREADY pushed an update carrying these very bullets
    // (`setAgentRole` → `applySessionModelPolicy(true)`), so recording them
    // without a second push is what keeps run start and stop to one payload
    // each rather than two.
    if (this.m_roleId !== roleBeforeChange) return;
    this.refreshSessionContextOrHold();
  }

  /**
   * **A move the learner makes is SAID in the conversation, not only in the
   * bullets.** The push above keeps the instructions right, but the model
   * answers from its conversation as much as from them, and a panel move left
   * nothing there: the conversation's last word on position stayed the agent's
   * own previous tool reply, and "replay the step" re-sent that jump (measured
   * 2026-09-11 — see `workflowRunUpdateNote.ts`).
   *
   * Posted as a context note: read, never answered, never shown in the chat,
   * never counted as the learner's turn — so it cannot release a script's
   * `wait_for user_response` either. The agent's own moves get none.
   */
  private handleWorkflowStepArrived(arrival: WorkflowStepArrival): void {
    const note = buildWorkflowRunUpdateNote(arrival);
    if (note === null) return;
    this.postWorkflowRunNote(note);
  }

  /**
   * **A run STARTING is said in the conversation too** (AI-1419). The bullets
   * already say a run is playing, but nothing clears the conversation between
   * runs — nor between projects — and after a run that finished through Spark
   * its `run_finished` reply and "the training is finished" outweighed them:
   * through the next run's Welcome the Guide kept saying it was over, and
   * answered "Let's start." with "only you can start it again".
   *
   * Runs after `handleWorkflowRunStateChanged` has handed the session to the
   * Guide, so a text-bridge role gets it in its fresh history, and before the
   * first step's narration. Best-effort like every note: a role switch that
   * reconnects the session drops it, and a new session has no stale run to
   * correct.
   */
  private handleWorkflowRunStarted(start: WorkflowRunStart): void {
    this.postWorkflowRunNote(buildWorkflowRunStartNote(start));
  }

  private postWorkflowRunNote(note: string): void {
    this.realtimeSessionModel.sendContextNote(note);
    // A role on the text bridge keeps its own history, which never sees the
    // realtime conversation's items.
    if (this.isTextBridgeActive()) this.m_textBridge.appendContextNote(note);
  }

  /**
   * Which role the workflow surfaces own, or null when they own none. Playback
   * outranks the editor: a preview started from the open panel still has to be
   * narrated by the Guide, and Stop drops back to the builder because the panel
   * the author left open is still the surface they are working in.
   */
  private resolveWorkflowRoleId(): string | null {
    if (this.m_isWorkflowRunPlaying) return PLAYBACK_ROLE_ID;
    if (this.m_isWorkflowEditorOpen) return WORKFLOW_BUILDER_ROLE_ID;
    return null;
  }

  /**
   * **A guided workflow run is narrated by the Guide, and the editor by the
   * Workflow Builder, whatever role the author was in.** Taking a role remembers
   * the one it interrupted; releasing the last surface hands that one back.
   *
   * Three things it deliberately does NOT do:
   * - It leaves the role alone when the account has withdrawn the role it wants,
   *   rather than letting `resolveEnabledRoleId` fall back to the default and
   *   demote an author mid-preview.
   * - It does not take or hand back a role with no project open. A run or panel
   *   torn down by a project unload also reaches here, and
   *   `resetRoleForProjectChange` has already settled what that leaves behind.
   * - It does not overwrite the remembered role when one surface hands to
   *   another, so Play from the open editor still restores what OPENING it
   *   interrupted rather than the builder.
   */
  private applyWorkflowAgentRole(): void {
    const desired = this.resolveWorkflowRoleId();
    if (this.m_deps.project.activeProject === null) return;

    if (desired !== null) {
      if (!this.interfaceModel.isRoleEnabled(desired)) return;
      if (this.m_roleId.toLowerCase() === desired) return;
      if (this.m_roleBeforeWorkflow === null) this.m_roleBeforeWorkflow = this.m_roleId;
      this.setAgentRole(desired);
      return;
    }

    const previous = this.m_roleBeforeWorkflow;
    this.m_roleBeforeWorkflow = null;
    if (previous === null) return;
    // Only hand it back if a workflow surface still holds the role: someone who
    // picked a different one meanwhile has already said what they want.
    const current = this.m_roleId.toLowerCase();
    if (current === PLAYBACK_ROLE_ID || current === WORKFLOW_BUILDER_ROLE_ID) {
      this.setAgentRole(previous);
    }
  }

  buildMainAgentSystemPrompt(): string {
    const projectPrompt = null; // Unity `ProjectData.ProjectPrompt` — DTO field not mapped on web yet.
    const firstName = this.m_deps.auth.currentUser?.firstName ?? null;
    this.interfaceModel.setActiveRoleId(this.m_roleId);
    const loadedSkills = this.interfaceModel.buildLoadedSkillsPromptSection();
    const availableSkills = this.interfaceModel.buildAvailableSkillsIndexSection();
    // Workflow run-state bullets are the first runtime-conversation instructions on web
    // (Unity routes them through the routing partial's runtime prompt).
    const runtimePrompt = this.workflowRunModel.buildRuntimePromptInstructions();
    return getMainAgentInstructions(
      this.m_roleId,
      this.m_personalityId,
      projectPrompt,
      runtimePrompt,
      firstName,
      loadedSkills,
      availableSkills,
    );
  }

  /**
   * There is no `# Current Workflow` block any more (Unity
   * `TryBuildWorkflowContext`). It carried the whole serialized workflow file on
   * every turn, for every role; the document is now read on demand through
   * `get_workflow`, which only the guide and workflow-builder roles hold. What
   * the run still injects is its STATE, through the runtime bullets in
   * `buildMainAgentSystemPrompt`.
   */
  private buildMainAgentInstructionsWithProjectContext(): string {
    const instructions = this.buildMainAgentSystemPrompt();
    const projectContext = this.contextEngineModel.tryBuildProjectContextForMessage();
    if (!projectContext) return instructions;
    return `${instructions}\n\n# Current Project Context\n${projectContext}`;
  }

  /**
   * Unity replay waits for finite highlight pulses, not merely for the
   * highlight tool's persistence task. The Web runtime clears the snapshot
   * payload when the final pulse completes, so that local model transition is
   * the authoritative completion signal here too.
   */
  private waitForWorkflowToolEffectsIdleAsync(
    toolId: string,
    args: ToolArgs,
  ): Promise<void> {
    if (toolId.toLowerCase() !== AgentToolNames.HighlightPart.toLowerCase()) {
      return Promise.resolve();
    }

    const sceneId = this.m_deps.project.activeSceneId;
    if (!sceneId) return Promise.resolve();

    const paths = getStringList(args, "partPathsToHighlight");
    const targetIds = new Set(
      paths.length > 0
        ? resolveManyByPath(this.m_deps.project.getEntities(sceneId), paths)
            .map((entry) => entry.snapshot.id)
        : this.m_deps.selection.selectedIds,
    );
    if (targetIds.size === 0) return Promise.resolve();

    const maxActivePulseCount = (): number => {
      let maxCount = 0;
      for (const snapshotId of targetIds) {
        const snapshot = this.m_deps.project.getEntitySnapshotById(sceneId, snapshotId);
        const highlight = parsePersistentHighlightJson(snapshot?.persistentHighlightJson);
        if (
          highlight?.pulseMode === PersistentHighlightPulseModes.Continuous
          && highlight.pulseCount !== null
        ) {
          maxCount = Math.max(maxCount, highlight.pulseCount);
        }
      }
      return maxCount;
    };

    const initialPulseCount = maxActivePulseCount();
    if (initialPulseCount === 0) return Promise.resolve();

    return new Promise<void>((resolve) => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let offEntityUpdated: () => void = () => {};
      let offActiveSceneChanged: () => void = () => {};
      const finish = () => {
        if (settled) return;
        settled = true;
        offEntityUpdated();
        offActiveSceneChanged();
        if (timer) clearTimeout(timer);
        resolve();
      };
      const check = () => {
        if (
          this.m_deps.project.activeSceneId !== sceneId
          || maxActivePulseCount() === 0
        ) {
          finish();
        }
      };

      offEntityUpdated = this.m_deps.project.onEntityUpdated.on((event) => {
        if (event.sceneId === sceneId && targetIds.has(event.snapshotId ?? "")) check();
      });
      offActiveSceneChanged = this.m_deps.project.onActiveSceneChanged.on(check);

      // Safety net for a headless/no-viewport host where no highlight runtime
      // is mounted to publish the auto-clear event. Each highlight carries its
      // own pulse length, so wait out the longest finite one.
      let longestPulseSeconds = 0;
      for (const snapshotId of targetIds) {
        const snapshot = this.m_deps.project.getEntitySnapshotById(sceneId, snapshotId);
        const highlight = parsePersistentHighlightJson(snapshot?.persistentHighlightJson);
        const duration = highlight ? highlightFinitePulseDurationSeconds(highlight) : null;
        if (duration !== null) longestPulseSeconds = Math.max(longestPulseSeconds, duration);
      }
      timer = setTimeout(finish, longestPulseSeconds * 1_000 + 1_000);
      check();
    });
  }

  /**
   * Resolves when response generation and local audio playback both finish.
   *
   * WebRTC can take the short settle only when the provider's output-buffer
   * stop and the local output meter independently agree. Missing either proof
   * keeps the former 500 ms settle, as do transports without that pair.
   */
  private waitForResponseIdleAsync(timeoutMs: number): Promise<void> {
    const session = this.realtimeSessionModel;
    return new Promise<void>((resolve) => {
      let sawActivity = session.hasActiveResponse || session.isAgentSpeaking;
      let settled = false;
      let idleSettleTimer: ReturnType<typeof setTimeout> | null = null;
      const cancelIdleSettle = () => {
        if (idleSettleTimer === null) return;
        clearTimeout(idleSettleTimer);
        idleSettleTimer = null;
      };
      const finish = () => {
        if (settled) return;
        settled = true;
        cancelIdleSettle();
        offResponse();
        offSpeaking();
        clearTimeout(timer);
        clearTimeout(graceTimer);
        resolve();
      };
      const check = () => {
        if (session.hasActiveResponse || session.isAgentSpeaking) {
          sawActivity = true;
          cancelIdleSettle();
          return;
        }
        if (!sawActivity || idleSettleTimer !== null) return;
        idleSettleTimer = setTimeout(() => {
          idleSettleTimer = null;
          if (session.hasActiveResponse || session.isAgentSpeaking) {
            sawActivity = true;
            return;
          }
          finish();
        }, session.hasPreciseAgentSpeechCompletion
          ? getVoiceSettings().timing.scriptedStepPostSpeechWaitMs
          : getVoiceSettings().timing.webScriptedStepFallbackPostSpeechWaitMs);
      };
      const offResponse = session.onActiveResponseChanged.on(check);
      const offSpeaking = session.onAgentSpeakingChanged.on(check);
      const timer = setTimeout(finish, timeoutMs);
      // No response ever started (disconnected session / text-only): don't hang the script.
      const graceTimer = setTimeout(() => {
        if (!sawActivity) finish();
      }, 5_000);
      check();
    });
  }

  /**
   * Resolves on the learner's next turn — typed or spoken — backing a workflow
   * script's `wait_for` step with `trigger: user_response`.
   *
   * BOTH input paths are watched, because which one carries a turn is a
   * property of how the learner is working, not of the script: a headset
   * learner speaks and a desktop one may type, and a wait that only heard one
   * of them would hang for the other. These are the same two emitters the
   * orchestrator already routes turns from, so nothing new is being listened
   * for — only awaited.
   */
  /**
   * Turns the learner has taken this session — typed or spoken.
   *
   * Counted continuously rather than only while something is listening,
   * because that is exactly the case a workflow `wait_for` has to answer: the
   * script asks its question in a `say` step, which is awaited on its own
   * before the loop reaches the wait, so a learner who answers over the
   * question does it in a window where no listener exists. The count survives
   * that window; a subscription cannot.
   */
  private m_userTurnCount = 0;

  /**
   * A NEW QUESTION CLEARS THE PREVIOUS QUESTION'S SCENE VISUAL. Each question
   * decides for itself what is on screen: a size question draws its box, a
   * clearance question draws the box and its ground line, and a question about
   * documents or a count draws nothing — so the previous answer's picture must
   * not be sitting under it.
   *
   * ⚠ This is an APP-SIDE rule on purpose, and the two obvious alternatives
   * both failed in front of the user. `Get_Part_Dimensions` is what draws, so
   * the picture only tracks the question while the agent keeps calling it — and
   * it does not have to: one answer often serves the follow-up ("how long is
   * it", then "how high is it"), and `base.md` correctly tells it to reuse
   * rather than refetch. A prompt rule telling it to call anyway held twice and
   * then did not; narrowing the payload so each question returns only its own
   * figures fixed that pair but not a conversation, because the transcript
   * accumulates and the answer is eventually in it either way. Nothing the
   * agent is TOLD can close that, so the app stops relying on it being told.
   *
   * Fires at the START of the turn, before the agent answers, so this
   * question's own drawing survives; and only for `agent` boxes, so a box the
   * user pinned with the Measure tool is not taken down by talking to Spark.
   */
  private clearAgentSceneVisualsForNewTurn(): void {
    if (!Ioc.isRegistered<IBoundingBoxToolModel>(BOUNDING_BOX_TOOL_MODEL)) return;
    Ioc.resolve<IBoundingBoxToolModel>(BOUNDING_BOX_TOOL_MODEL).clearBoundingBoxes("agent");
  }

  private waitForUserTurnAsync(
    sinceTurnCount: number,
    timeoutMs: number,
    signal: AbortSignal,
  ): Promise<boolean> {
    // Already answered — while the question was still being spoken, most
    // often. The counter is what makes that observable at all: the say step
    // was awaited before the wait step existed, so nothing was listening then.
    if (this.m_userTurnCount > sinceTurnCount) return Promise.resolve(true);

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (turnArrived: boolean): void => {
        if (settled) return;
        settled = true;
        offText();
        offVoice();
        clearTimeout(timer);
        signal.removeEventListener("abort", onAbort);
        resolve(turnArrived);
      };
      const onAbort = (): void => finish(false);

      const offText = this.interfaceModel.onUserTextSubmitted.on(() => finish(true));
      // A transcript can settle as an empty string when the mic caught noise;
      // that is not the learner answering, so it must not release the wait.
      const offVoice = this.realtimeSessionModel.onUserTranscriptCompleted.on((transcript) => {
        if (transcript.trim().length > 0) finish(true);
      });
      const timer = setTimeout(() => finish(false), timeoutMs);

      if (signal.aborted) onAbort();
      else signal.addEventListener("abort", onAbort, { once: true });
    });
  }

  async setAgentPersonality(personalityId: string): Promise<boolean> {
    const personality = getPersonality(personalityId);
    if (personality.id.toLowerCase() !== personalityId.trim().toLowerCase() &&
        personality.displayName.toLowerCase() !== personalityId.trim().toLowerCase()) {
      return false;
    }
    if (personality.id === this.m_personalityId) return true;
    this.m_personalityId = personality.id;
    safeWritePref(PREF_PERSONALITY, personality.id);
    this.onCurrentPersonalityChanged.emit(personality.id);
    // Voice changes require a fresh session (Unity reconnects too).
    await this.realtimeSessionModel.restartAsync("personality-change");
    return true;
  }

  async resetConversation(): Promise<boolean> {
    this.resetTextBridge();
    this.m_deps.transcript.resetConversation();
    return await this.realtimeSessionModel.restartAsync("reset-conversation");
  }

  // -- Lifecycle ---------------------------------------------------------

  async startAsync(): Promise<void> {
    if (!isAiEnabled()) return;
    // Load + register the data-driven prompt content BEFORE the first session connect so the
    // first prompt is complete (Unity `AIRealtimeSessionModel.Start()` order: base prompt,
    // personalities, then `interfaceModel.EnsureContentLoadedAsync()` = skills + roles — the
    // interface model registers the roles onto agentPrompts and refreshes the session on late
    // loads). Catalog loads are idempotent-cached, so reconnect and personality-restart paths
    // that rebuild the session options see the loaded content.
    await ensureVoiceSettingsLoadedAsync();
    await ensureBasePromptLoadedAsync();
    await ensurePersonalitiesLoadedAsync();
    await this.interfaceModel.ensureContentLoadedAsync();
    await this.realtimeSessionModel.startAsync();
  }

  private async syncWorkflowDocumentsForSearchAsync(
    event: WorkflowChangedEvent,
    source: "background",
  ): Promise<void> {
    if (!isWorkflowKnowledgeChangeOrigin(event.origin)) return;
    const vectorStore = this.m_deps.vectorStore;
    if (!vectorStore) return;
    const workflowId = event.document?.uid.trim() ?? "";
    if (!workflowId) return;
    const project = this.m_deps.project.activeProject;
    if (!project || event.projectId !== project.id) return;
    const customerId = project?.customerId ?? this.m_deps.auth.currentUser?.customerId;
    if (!customerId) return;

    try {
      if (event.origin === "load") {
        // Start this before document discovery so a question arriving during
        // project load can join the in-flight lookup instead of starting it.
        const vectorStoreId = await vectorStore.getOrCreateVectorStoreIdAsync(customerId);
        if (!vectorStoreId) {
          console.info("[AIOrchestrator] workflow vector-store preparation skipped (background)");
          return;
        }
      }

      // `ProjectDocsModel` is the one owner of workflow/product document
      // association. Force it to re-evaluate after `workflow.yaml` changes, then
      // sync only rows tagged as belonging to THIS workflow.
      const items = await this.m_deps.docs.getProjectDocsAsync(true);
      const workflowItems = items
        ? items.flatMap((item) => {
          const belongsToWorkflow = item.sources.some((docSource) => (
            docSource.kind === "workflow"
              ? docSource.workflowId === workflowId
              : true
          ));
          if (!belongsToWorkflow) return [];
          return [{
            storagePath: item.storagePath,
            fileName: item.fileName,
            fileSize: item.fileSize,
            lastModifiedMs: item.lastModifiedMs,
            knowledgeKey: normalizeProductNameKey(item.documentNameKey),
          }];
        })
        : [];

      const result = await vectorStore.syncWorkflowDocumentsAsync(
        customerId,
        workflowId,
        workflowItems,
      );
      if (result.skipped) {
        console.info(
          `[AIOrchestrator] workflow document vector sync skipped (${source}): ${result.reason ?? "unknown"}`,
        );
      } else if (result.uploaded > 0 || result.deleted > 0 || result.failed > 0) {
        console.info(
          `[AIOrchestrator] workflow document vector sync (${source}): checked=${result.checked} current=${result.current} uploaded=${result.uploaded} deleted=${result.deleted} failed=${result.failed}`,
        );
      }
    } catch (error) {
      console.warn("[AIOrchestrator] document vector sync failed:", error);
    }
  }

  stop(): void {
    this.realtimeSessionModel.stop();
  }

  dispose(): void {
    this.stop();
    this.cancelProjectEntrySettling();
    this.clearNarrationRefreshHoldTimer();
    this.m_isNarrationRefreshHeld = false;
    this.m_hasHeldNarrationRefresh = false;
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.workflowRunModel.dispose();
    this.workflowScriptRunner.dispose();
    this.contextEngineModel.dispose();
  }

  /**
   * `AIGuardrailsModel.Permission.CanCurrentUserWrite` — the role half of the
   * `writerOnly` tool gate, now reading a REAL role. It returned true
   * unconditionally until `ProjectModel.activeUserRole` stopped being
   * hardcoded `"owner"`, so none of the 26 `writerOnly` tools had ever been
   * refused. Owner|Editor, via the one shared predicate.
   */
  private isWriter(): boolean {
    return canAuthorProject(this.m_deps.project.activeUserRole);
  }
}

function safeReadPref(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWritePref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable — personality stays session-only.
  }
}

export const AI_ORCHESTRATOR_MODEL = Symbol.for("Campfire.AIOrchestratorModel");
